import type { Season } from '../types/workout';
import { supabase } from './supabase';

const configuredBackendUrl = import.meta.env.VITE_BACKEND_API_URL;
if (!configuredBackendUrl) {
  throw new Error(
    'VITE_BACKEND_API_URL is not set. Set it in admin/.env.local (see admin/.env.example). ' +
      'The admin never falls back to the production backend.',
  );
}
export const BACKEND_API_URL: string = configuredBackendUrl;

export async function authHeaders(contentType = 'application/json'): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (contentType) headers['Content-Type'] = contentType;
  const session = (await supabase?.auth.getSession())?.data?.session;
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

const seasonHashes = new Map<string, string>();

export function getKnownSeasonHash(userId: string): string | undefined {
  return seasonHashes.get(userId);
}

/**
 * Load a user's workout season from the backend, remembering its hash so the
 * next save is conditional on it.
 */
export async function loadSeason(userId: string): Promise<Season | null> {
  const response = await fetch(`${BACKEND_API_URL}/admin/user_season/${encodeURIComponent(userId)}`, {
    headers: await authHeaders(),
  });

  if (!response.ok) {
    if (response.status === 404) {
      seasonHashes.delete(userId);
      return null;
    }
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Failed to load season');
  }

  const data = await response.json();
  if (data.season_hash) {
    seasonHashes.set(userId, data.season_hash);
  }
  return data.season;
}

/**
 * Save (overwrite) a user's workout season, conditional on the hash of the
 * season last loaded or saved. A 412 means the member's plan changed since.
 */
export async function saveSeason(
  userId: string,
  season: Season,
  expectedHash?: string
): Promise<void> {
  const seasonHash = expectedHash ?? seasonHashes.get(userId);
  if (!seasonHash) {
    throw new Error('Reload this season before saving: no loaded version to save against.');
  }
  const response = await fetch(`${BACKEND_API_URL}/admin/save_user_season/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'If-Match': seasonHash },
    body: JSON.stringify({ season }),
  });

  if (response.status === 412) {
    throw new Error('This plan changed since it was loaded. Reload the season and reapply your edit.');
  }
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Failed to save season');
  }
  const data = await response.json();
  if (data.season_hash) {
    seasonHashes.set(userId, data.season_hash);
  }
}

/**
 * Generate a new workout season for a user using AI (admin action).
 *
 * Hits POST /admin/build_user_season/{user_id}, NOT the user-facing
 * /build_workout_season. The user-facing endpoint gates on the caller's
 * subscription_status (admins don't carry one) AND ignores user_id in
 * the body — both fatal for admin "Generate" actions. The admin endpoint
 * takes user_id as a path param and skips the subscription gate.
 */
export async function generateSeason(
  userId: string,
  overwrite: boolean = false
): Promise<{ response: string; season: Season }> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/build_user_season/${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ overwrite }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Failed to generate season');
  }

  // 202 kickoff (generation runs 7-8 min in a background worker — beyond
  // Railway's 5-min edge cap). Poll until terminal, then reload the season
  // so callers keep the original {response, season} contract.
  await pollGenerationUntilDone(userId, 'Season generation');
  const season = await loadSeason(userId);
  if (!season) throw new Error('Generation finished but season could not be reloaded');
  return { response: 'Season generated successfully', season };
}

/**
 * Test exercise principles by generating a workout plan (not saved)
 */
export async function testGeneration(
  userProfile: Record<string, any>,
  seasonStructureInstructions?: string
): Promise<{ response: string; season: Season; test_mode: boolean; note: string }> {
  const response = await fetch(`${BACKEND_API_URL}/admin/test_generation`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      user_id: userProfile.id,
      ...(seasonStructureInstructions ? { season_structure_instructions: seasonStructureInstructions } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to test generation');
  }

  return response.json();
}

/**
 * Load current season structure instructions from Supabase Storage
 */
export async function loadSeasonStructure(): Promise<string> {
  const response = await fetch(`${BACKEND_API_URL}/admin/season_structure`, {
    headers: await authHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to load season structure instructions');
  }

  const data = await response.json();
  return data.content;
}

/**
 * Update season structure instructions in Supabase Storage
 */
export async function updateSeasonStructure(
  content: string
): Promise<{ message: string; file_path: string; note: string }> {
  const response = await fetch(`${BACKEND_API_URL}/admin/update_season_structure`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ content }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update season structure instructions');
  }

  return response.json();
}

/**
 * List users, optionally filtered by name OR email substring.
 */
export interface AdminUserListEntry {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  selected_activities?: string[];
}

export async function getUsers(search?: string): Promise<AdminUserListEntry[]> {
  const url = new URL(`${BACKEND_API_URL}/admin/users`);
  if (search) url.searchParams.set('search', search);

  const response = await fetch(url.toString(), {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch users');

  const data = await response.json();
  return data.users;
}

/**
 * Fetch full profile for a given user_id
 */
export async function getUserProfile(userId: string): Promise<Record<string, any>> {
  const response = await fetch(`${BACKEND_API_URL}/admin/user_profile/${userId}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch user profile');
  const data = await response.json();
  return data.profile;
}

/**
 * Chat image attachment that a user uploaded via the mobile chat flow.
 * Sourced from the `chat-attachments` Supabase Storage bucket; URLs are
 * short-lived (1 hour) signed URLs minted server-side for the admin.
 */
export interface UserChatAttachment {
  conversation_id: string | null;
  filename: string;
  path: string;
  signed_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  size_bytes: number | null;
  mime_type: string | null;
}

export interface UserChatAttachmentsResponse {
  user_id: string;
  count: number;
  images: UserChatAttachment[];
}

export async function getUserChatAttachments(
  userId: string,
): Promise<UserChatAttachmentsResponse> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user_chat_attachments/${encodeURIComponent(userId)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load chat attachments');
  }
  return response.json();
}

/**
 * Patch a user profile. Backend whitelists editable fields in
 * services/profile_service.ADMIN_EDITABLE_PROFILE_FIELDS — unknown keys are
 * silently dropped server-side, but we trim them client-side too so the wire
 * payload stays small.
 */
export const ADMIN_EDITABLE_PROFILE_FIELDS = [
  'first_name',
  'last_name',
  'phone_number',
  'age',
  'gender',
  'location',
  'height_feet',
  'height_inches',
  'weight_lbs',
  'selected_activities',
  'training_schedule',
  'preferred_days',
  'goals',
  'equipment',
  'studio_preferences',
  'profile_summary',
  'profile_image_url',
  'date_of_birth',
] as const;

export type AdminEditableField = (typeof ADMIN_EDITABLE_PROFILE_FIELDS)[number];

export async function updateUserProfile(
  userId: string,
  patch: Partial<Record<AdminEditableField, unknown>>,
): Promise<Record<string, any>> {
  const filtered: Record<string, unknown> = {};
  for (const key of ADMIN_EDITABLE_PROFILE_FIELDS) {
    if (key in patch) filtered[key] = patch[key];
  }
  const response = await fetch(`${BACKEND_API_URL}/admin/update_profile/${userId}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(filtered),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to update profile');
  }
  const data = await response.json();
  return data.profile;
}

/**
 * Edit a user's workout season via a chat message.
 */
export async function chatEdit(
  userId: string,
  message: string,
  conversationHistory: { role: 'user' | 'assistant'; content: string }[]
): Promise<{ response: string; season: Season }> {
  const response = await fetch(`${BACKEND_API_URL}/admin/chat_edit`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      user_id: userId,
      message,
      conversation_history: conversationHistory,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Chat edit failed');
  }

  return response.json();
}

// Philosophy Sections (A-G)

export const PHILOSOPHY_SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
export type PhilosophySection = (typeof PHILOSOPHY_SECTIONS)[number];

export const PHILOSOPHY_LABELS: Record<PhilosophySection, string> = {
  A: 'Recovery & Readiness',
  B: 'Periodization & Phases',
  C: 'Exercise Selection',
  D: 'Volume & Intensity',
  E: 'Pairing & Grouping',
  F: 'Progressive Overload',
  G: 'Safety Rules',
};

export async function loadAllPhilosophySections(): Promise<
  Record<string, string | null>
> {
  const response = await fetch(`${BACKEND_API_URL}/admin/philosophy`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to load philosophy sections');
  }
  const data = await response.json();
  return data.sections;
}

export async function loadPhilosophySection(
  section: PhilosophySection,
): Promise<string> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/philosophy/${section}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    if (response.status === 404) return '';
    const error = await response.json();
    throw new Error(error.error || `Failed to load section ${section}`);
  }
  const data = await response.json();
  return data.content;
}

export async function updatePhilosophySection(
  section: PhilosophySection,
  content: string,
): Promise<{ message: string; file_path: string; note: string }> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/philosophy/${section}`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ content }),
    },
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to update section ${section}`);
  }
  return response.json();
}

// Media

export interface ExerciseMedia {
  id: string;
  exercise_name: string;
  video_filename: string | null;
  image_filename: string | null;
}

export interface ExerciseLibraryEntry {
  exercise_name: string;
  movement_pattern: string;
  equipment: string;
  has_video: boolean;
  has_image: boolean;
  video_filename: string | null;
  image_filename: string | null;
  youtube_id: string | null;
  youtube_url: string | null;
}

export interface ExerciseLibraryResponse {
  exercises: ExerciseLibraryEntry[];
  total: number;
  with_video: number;
  with_image: number;
  with_youtube: number;
}

export async function getExerciseLibraryWithMedia(): Promise<ExerciseLibraryResponse> {
  const response = await fetch(`${BACKEND_API_URL}/exercise_library_with_media`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch exercise library');
  return response.json();
}

export interface ExerciseMediaWithUrls extends ExerciseMedia {
  image_url: string | null;
  video_url: string | null;
}

export async function getExerciseMediaUrls(exerciseName: string): Promise<ExerciseMediaWithUrls> {
  const response = await fetch(`${BACKEND_API_URL}/exercise_media/${encodeURIComponent(exerciseName)}/urls`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch media URLs');
  return response.json();
}

export async function uploadExerciseMedia(
  exerciseName: string,
  file: File,
  mediaType: 'image' | 'video',
): Promise<{ message: string; filename: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('media_type', mediaType);
  const response = await fetch(
    `${BACKEND_API_URL}/exercise_media/${encodeURIComponent(exerciseName)}/upload`,
    {
      method: 'POST',
      headers: await authHeaders(''),
      body: formData,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(err.detail || 'Upload failed');
  }
  return response.json();
}

export async function deleteExerciseMedia(
  exerciseName: string,
  mediaType: 'image' | 'video',
): Promise<{ message: string }> {
  const response = await fetch(
    `${BACKEND_API_URL}/exercise_media/${encodeURIComponent(exerciseName)}/${mediaType}`,
    {
      method: 'DELETE',
      headers: await authHeaders(),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Delete failed' }));
    throw new Error(err.detail || 'Delete failed');
  }
  return response.json();
}

export async function bulkAssignExerciseMedia(
  exerciseNames: string[],
  file: File,
  mediaType: 'image' | 'video',
): Promise<{ count: number; filename: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('media_type', mediaType);
  formData.append('exercise_names', JSON.stringify(exerciseNames));
  const response = await fetch(`${BACKEND_API_URL}/exercise_media/bulk_assign`, {
    method: 'POST',
    headers: await authHeaders(''),
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Bulk assign failed' }));
    throw new Error(err.detail || 'Bulk assign failed');
  }
  return response.json();
}

export async function bulkClearExerciseMedia(
  exerciseNames: string[],
  mediaType: 'image' | 'video',
): Promise<{ count: number }> {
  const response = await fetch(`${BACKEND_API_URL}/exercise_media/bulk_clear`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ exercise_names: exerciseNames, media_type: mediaType }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Bulk clear failed' }));
    throw new Error(err.detail || 'Bulk clear failed');
  }
  return response.json();
}


// System Prompt Inspector

export type PromptKind = 'generation' | 'edit' | 'chat' | 'booking' | 'reviewer';

export interface PromptInfo {
  text: string;
  char_count: number;
  source_file: string;
  description: string;
  philosophy_sections: string[];
  has_override?: boolean;
  override_text?: string;
  supported_placeholders?: string[];
}

export async function getAllPrompts(): Promise<Record<PromptKind, PromptInfo>> {
  const response = await fetch(`${BACKEND_API_URL}/admin/prompts`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load system prompts');
  const data = await response.json();
  return data.prompts as Record<PromptKind, PromptInfo>;
}

export async function previewPrompt(
  kind: PromptKind,
  userId: string,
): Promise<PromptInfo> {
  const url = new URL(`${BACKEND_API_URL}/admin/prompts/${kind}/preview`);
  url.searchParams.set('user_id', userId);
  const response = await fetch(url.toString(), {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to preview prompt');
  }
  const data = await response.json();
  return {
    text: data.text,
    char_count: data.char_count,
    source_file: data.source_file,
    description: data.description,
    philosophy_sections: data.philosophy_sections ?? [],
  };
}

export interface SandboxMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function sandboxChat(
  systemPrompt: string,
  messages: SandboxMessage[],
): Promise<any> {
  const response = await fetch(`${BACKEND_API_URL}/admin/sandbox/chat`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ system_prompt: systemPrompt, messages }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Sandbox call failed');
  }
  return response.json();
}

// Test Generation Playground (inline profile, no DB user)

/** Goal sub-types collected per goal in mobile onboarding. */
export interface PlaygroundGoal {
  /** Human label shown to the agent (e.g. "Build muscle"). */
  name?: string;
  /** Machine slug — gain-muscle | lose-body-fat | get-faster | get-stronger | train-event | push-myself. */
  type?: string;
  /** Free-text "Tell us more..." captured per goal. */
  details?: string;
  muscleGroups?: string[];
  goalType?: string;
  targetPace?: { minutes?: number; seconds?: number; unit?: string };
  currentMuscleMass?: number | null;
  targetMuscleMass?: number | null;
  currentBodyFat?: number | null;
  targetBodyFat?: number | null;
  eventType?: string;
  eventMonth?: string;
  eventYear?: number | null;
  trainingStatus?: string;
  currentStatus?: string;
  pushType?: string;
}

export interface PlaygroundProfile {
  first_name?: string;
  last_name?: string;
  age?: number | null;
  date_of_birth?: string;
  gender?: string;
  height_feet?: number | null;
  height_inches?: number | null;
  weight_lbs?: number | null;
  location?: string;
  selected_activities?: string[];
  /** Sport(s) the user trains for — triggers Sport-Specific archetype via
   *  archetype_service._rule_1_sport_focus when non-empty. Mobile sends
   *  this as a list when ≥1 sport is picked. */
  sport_focus?: string[];
  training_schedule?: Record<string, number>;
  preferred_days?: Record<string, string[]>;
  goals?: PlaygroundGoal[];
  equipment?: string[];
  /** Mirrors mobile onboarding nesting; dev's extract_weightlifting_maxes
   *  reads onboarding_data.weightlifting.maxes (squat/bench/deadlift in lbs).
   *  maxes_provided is the source-of-truth flag the FIRST-WEEK WEIGHT
   *  ANCHORING spec keys its 130-lb sentinel handling on. */
  onboarding_data?: {
    weightlifting?: {
      maxes?: { squat?: number; bench?: number; deadlift?: number };
      maxes_provided?: boolean;
    };
  };
  profile_summary?: string;
}

/** Coarse per-metric readiness level for the synthetic wearable. */
export type ReadinessLevel = 'green' | 'yellow' | 'red';

/** Synthetic wearable inputs — mirrors backend PlaygroundWearable. Each metric
 *  is a coarse level; the backend maps it to representative values and runs the
 *  REAL compute_readiness_state scorer so generation sees an authentic signal. */
export interface PlaygroundWearable {
  enabled: boolean;
  hrv_level?: ReadinessLevel;
  rhr_level?: ReadinessLevel;
  sleep_level?: ReadinessLevel;
  subjective_level?: ReadinessLevel;
}

/** The deterministic compute_readiness_state verdict for the synthetic inputs. */
export interface ComputedReadiness {
  state: 'GREEN' | 'YELLOW' | 'RED';
  cause: string;
  modification_options: string[];
  data_quality?: string | null;
}

export interface PlaygroundResult {
  response: string;
  season: Season;
  test_mode: boolean;
  profile_used: Record<string, unknown>;
  /** What the deterministic classifier said BEFORE any override. */
  computed_archetype?: string;
  /** The archetype actually used for this generation (override if set,
   *  otherwise == computed_archetype). */
  effective_archetype?: string;
  /** The real readiness scorer's verdict on the synthetic wearable inputs
   *  (null when no wearable was simulated). */
  computed_readiness?: ComputedReadiness | null;
  /** Non-fatal fidelity warnings (e.g. date drift) surfaced, not thrown. */
  validation_warnings?: string[];
  note: string;
}

export async function runPlaygroundGeneration(args: {
  profile: PlaygroundProfile;
  principles?: string;
  seasonStructure?: string;
  /** Number of weeks to generate (1-4). Backend defaults to 1 if omitted. */
  weeksToGenerate?: number;
  /** When non-empty, REPLACES the whole system prompt for this run (bypasses
   *  the default builder + principles + exercise library). For A/B testing
   *  new prompts without redeploying the backend. */
  systemPrompt?: string;
  /** Force a specific archetype for this run. Backend ignores empty/unset. */
  archetypeOverride?: string;
  /** Synthetic wearable/readiness signal. Omit (or enabled:false) for a
   *  no-wearable run (defaults to GREEN). */
  wearable?: PlaygroundWearable;
}): Promise<PlaygroundResult> {
  const body: Record<string, unknown> = { profile: args.profile };
  if (args.principles && args.principles.trim()) body.content = args.principles;
  if (args.seasonStructure && args.seasonStructure.trim()) {
    body.season_structure_instructions = args.seasonStructure;
  }
  if (typeof args.weeksToGenerate === 'number' && args.weeksToGenerate > 0) {
    body.weeks_to_generate = args.weeksToGenerate;
  }
  if (args.systemPrompt && args.systemPrompt.trim()) {
    body.system_prompt = args.systemPrompt;
  }
  if (args.archetypeOverride && args.archetypeOverride.trim()) {
    body.archetype_override = args.archetypeOverride;
  }
  if (args.wearable && args.wearable.enabled) {
    body.wearable = args.wearable;
  }
  const response = await fetch(
    `${BACKEND_API_URL}/admin/playground/test_generation`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Playground generation failed');
  }
  return response.json();
}

// Activity Log

export interface ActivityLogEntry {
  id: string;
  user_id: string;
  event_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  user_name: string;
}

export async function getActivityLog(limit = 50): Promise<ActivityLogEntry[]> {
  const url = new URL(`${BACKEND_API_URL}/admin/activity_log`);
  url.searchParams.set('limit', String(limit));
  const response = await fetch(url.toString(), {
    headers: await authHeaders(),
  });
  if (!response.ok) return [];
  const data = await response.json();
  return data.entries;
}

// Seasons Overview

export interface SeasonOverviewUser {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  selected_activities?: string[];
  /** ISO timestamp of the user's last app open/foreground (POST /me/heartbeat). Null = never seen. */
  last_seen_at?: string | null;
  dates: Record<string, 'workout' | 'rest'>;
}

export async function getSeasonsOverview(): Promise<SeasonOverviewUser[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/seasons_overview`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load seasons overview');
  const data = await response.json();
  return data.users;
}

// Booking-agent SLA

export interface BookingSlaMetrics {
  window_days: number;
  total_attempts: number;
  resolved: number;
  confirmed: number;
  success_rate_pct: number | null;
  status_breakdown: Record<string, number>;
  latency_ms: { p50: number | null; p90: number | null; p99: number | null; sample_size: number };
  avg_iterations: number | null;
  tokens: { input: number; output: number; total: number };
}

/** Booking-agent SLA over the last N days, computed from persisted telemetry. */
export async function getBookingSlaMetrics(days = 30, cohorts?: number[]): Promise<BookingSlaMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/bookings/sla_metrics?days=${days}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load booking SLA metrics');
  return response.json();
}

// Admin→PostHog metrics proxy

export interface PostHogMetrics {
  configured: boolean;
  source?: string;
  dau?: number | null;
  wau?: number | null;
  stickiness?: number | null;
  workouts_completed_7d?: number | null;
  /**
   * The edit-rate window only. DAU/WAU and workouts_completed_7d are
   * definitions, not windows, so they ignore it.
   */
  window_days?: number | null;
  edit_rate_pct?: number | null;
  edits?: number | null;
  generated?: number | null;
  error?: string;
  /**
   * When PostHog is down the backend serves the last good answer. Same
   * contract as the other PostHog cards: a timestamp here means the numbers
   * are real but old, and the dashboard must say so.
   */
  stale_as_of?: string | null;
}

export interface StudioLeaderboardRow {
  studio_id: string;
  studio_name: string;
  count: number;
  confirmed: number;
  /** Distinct members with >=1 confirmed booking here. */
  unique_users: number;
  is_partner: boolean;
  partner_tier: "founding" | "non_founding" | "none" | null;
}

/** One studio on the pilot roster — the five in the agentic-booking PRD. */
export interface PilotStudioRow {
  studio_id: string;
  studio_name: string;
  /** Booking platform the front desk logs into: mindbody | mariana_tek. */
  platform: string | null;
  /** THE metric: distinct members with >=1 confirmed booking, any rail. */
  unique_users: number;
  confirmed: number;
  count: number;
  /** How it was placed, not whether it counts. */
  direct_api_confirmed: number;
  api_fallback_count: number;
  direct_api_enabled: boolean;
}

export interface PilotSummary {
  /** Distinct members across the whole pilot — not the sum of the rows. */
  unique_users: number;
  confirmed: number;
  studios: PilotStudioRow[];
}

export interface BookingSummary {
  window_days: number;
  total: number;
  confirmed: number;
  resolved: number;
  conversion_rate_pct: number | null;
  status_breakdown: Record<string, number>;
  by_studio: StudioLeaderboardRow[];
  pilot: PilotSummary;
}

/** Booking volume + conversion + per-studio leaderboard (DB-sourced). */
export async function getBookingSummary(days = 30, cohorts?: number[]): Promise<BookingSummary> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/bookings/summary?days=${days}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load booking summary');
  return response.json();
}

export interface ChatSummary {
  window_days: number;
  messages: number;
  user_messages: number;
  sessions: number;
  unique_users: number;
  msgs_per_session: number | null;
}

/** Chat volume — sessions, messages, unique users (DB-sourced). */
export async function getChatSummary(days = 30, cohorts?: number[]): Promise<ChatSummary> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/chat/summary?days=${days}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load chat summary');
  return response.json();
}

// AI generations drill-down (analytics, PostHog-sourced)

export interface AiGenerationsBreakdown {
  configured: boolean;
  error?: string;
  /**
   * Set when PostHog stopped answering and these are the last numbers it gave
   * us. Real, just old — render `<StaleBanner asOf={...} />` or the card claims
   * they're current.
   */
  stale_as_of?: string | null;
  window_days?: number;
  by_type?: { generated: number; extended: number; overwritten: number };
  reliability?: {
    succeeded: number;
    failed: number;
    fallback_used: number;
    success_rate_pct: number | null;
  };
  compute_ms?: {
    p50: number | null;
    p90: number | null;
    p99: number | null;
    avg: number | null;
    sample_size: number | null;
  };
  by_source?: { source: string; count: number }[];
  daily?: { date: string; generated: number; extended: number; overwritten: number }[];
  by_user?: { user: string; count: number }[];
}

/**
 * Onboarding Metrics: signup drop-off, chat-to-first-plan, member roster.
 *
 * Behaviour comes from PostHog, identity and ground truth (name, plan, season,
 * goals, wearables) from Supabase. The join happens server-side so nothing
 * here ever renders a UUID.
 */
export interface OnboardingMetrics {
  configured: boolean;
  error?: string;
  /**
   * PostHog went quiet and the behavioural half of this card came from cache.
   * The DB half (access stages, named members) is always current. See
   * `StaleBanner`.
   */
  stale_as_of?: string | null;
  /**
   * First day any signup screen reported (2026-04-09). The funnel is all-time —
   * there is no window — so the card dates itself from here.
   */
  funnel_since?: string | null;
  /** Waitlist → approved → account. All-time, all from the DB, all named. */
  access?: {
    stages: {
      label: string;
      reached: number;
      dropped: number;
      continued_users: string[];
      dropped_users: string[];
      /** Names beyond the cap on the (long) not-approved list. */
      dropped_overflow: number;
      is_final: boolean;
    }[];
    /**
     * Saw the subscribe wall and never tapped a plan or Subscribe.
     * `views` is how many times they were shown it.
     */
    paywall?: {
      saw_no_option: number;
      /** Identified sessions we could not resolve to a name. */
      unnamed?: number;
      users: { name: string; views: number }[];
      /** Extra HogQL failed — do not read a zero as "nobody saw it". */
      query_failed?: boolean;
    };
  };
  /** One bar per signup screen. `dropped` = saw it, never reached a later one. */
  funnel?: {
    screen: string;
    label: string;
    reached: number;
    dropped: number;
    continued_users: string[];
    dropped_users: string[];
    /** People we can't name — they never signed in, so there's no profile. */
    anonymous_reached: number;
    /**
     * Why this bar's unnamed people are unnamed. Three unrelated situations
     * that used to be lumped as one "anonymous" count.
     */
    unnamed_why?: {
      /** No user id at all — never got past the phone screen. */
      never_signed_in: number;
      /** Signed in once; no account of theirs is left. */
      deleted: number;
      /** Account exists, name doesn't (and no waitlist row matched the phone). */
      no_name_on_file: number;
    };
    /**
     * Day this screen started reporting. Not the same for every bar — the Name
     * screen only from 2026-06-18 — so a short bar can mean "few people stop
     * here" or "we only started watching in June", and the card must say which.
     */
    tracked_since?: string | null;
    is_final: boolean;
  }[];
  /**
   * Sessions that reported a screen sitting behind the phone login without ever
   * logging in — App Store review walking the binary, mostly. Held out of every
   * bar (they'd make a later bar taller than an earlier one) and reported here
   * instead of being silently dropped.
   */
  unattributed_sessions?: number;
  /**
   * How many of the last bar are counted from their account row alone — members
   * who signed up before screen tracking existed, so no session backs them.
   */
  members_untracked?: number;
  /**
   * Finished signup and later deleted the account. They own no bar — filing them
   * on an earlier screen would call a finished signup a drop-off, and no member
   * count can hold them — so the card states them beside the bars.
   */
  got_in_then_left?: number;
  got_in_then_left_users?: string[];
  /**
   * Accounts that no longer exist, split by who deleted them. `members` is the
   * only one that means anything — a person who chose to leave — and the only
   * one still standing in the bars. `internal_cleanup` is us scrapping a
   * walkthrough and re-running it, which used to add a body to the funnel every
   * single time — the team counts as ordinary members everywhere else, this is
   * the one place we're held out; `unattributed` is a deletion older than the
   * events that record who did it. Both are out of the bars and named so they're
   * excluded in the open.
   */
  deletions?: {
    members: number;
    /**
     * Dates (YYYY-MM-DD, Pacific) of the members above who erased their own
     * account, newest first. This is the entire record we keep of a member
     * erasure — that one happened, and when. No name, deliberately: retaining
     * that is the exact thing the erasure was for.
     */
    member_dates?: string[];
    internal_cleanup: number;
    unattributed: number;
  };
  /** Our own test accounts, dropped from every number on this dashboard only. */
  internal_excluded?: { sessions: number; accounts: number };
  /**
   * Each member at the FIRST thing they're missing, so the bars sum to the
   * member base and each label names that member's actual blocker.
   */
  chat?: {
    key: string;
    label: string;
    /** Members whose one blocker is this. Exactly the people in `users`. */
    blocked_here: number;
    users: string[];
    is_success: boolean;
    /**
     * The coach card that is supposed to collect this thing, when one exists.
     * Different denominator from `blocked_here` on purpose — a blocker counts
     * each member once at their first hole, a card counts everyone who saw it —
     * so it rides under the bar as a line, never as part of it.
     */
    sheet?: {
      labels: string[];
      saw: number;
      completed: number;
      dropped_users: string[];
    };
    /**
     * Goal row only: how the members who HAVE a goal actually got one, keyed by
     * `goals.origin` (`chat` = the coach asked in conversation, `profile` = they
     * filled it in on their own Goals screen). Counts MEMBERS, once each, at
     * their first goal — so it never mixes with the card's `saw`/`completed`.
     */
    filled_by?: Record<string, number>;
  }[];
  /**
   * Coach chat cards after the short signup chain. `saw` = the card was
   * opened; `completed` = they submitted it or the profile already has
   * that answer.
   */
  sheets?: {
    key: string;
    label: string;
    saw: number;
    completed: number;
    saw_users: string[];
    completed_users: string[];
    dropped_users: string[];
    /** This card feeds a plan blocker, so it's shown on that row instead. */
    gates_plan?: boolean;
  }[];
  /**
   * Accounts started but never finished onboarding, so they're left out of the
   * plan-blocker card on purpose — their blocker is signup, not the coach.
   */
  stalled_in_signup?: number;
  stalled_in_signup_users?: string[];
  /** Of the stalled: comped rows the Jul-10 wipe emptied, not real drop-outs. */
  wiped_onboarding?: number;
  /**
   * Left out of every count above: reached the name screen without ever being
   * approved for access. "Accounts" means accounts we let in, here and on the
   * dashboard's KPI strip. Named so the roster reconciles against the profiles
   * table instead of quietly falling short of it.
   */
  never_approved?: number;
  never_approved_users?: string[];
  wearable_brands?: { provider: string; people: number }[];
  members?: {
    id: string;
    /** Finished onboarding — i.e. counts toward `total_members`. */
    got_in: boolean;
    name: string;
    plan: string | null;
    status: string | null;
    signup: string | null;
    has_season: boolean;
    last_active: string | null;
    active_today: boolean;
    events_today: number;
    /** What they've actually been doing, in plain words. */
    doing: string[];
    wearables: string[];
    messages: number;
  }[];
  active_today?: number;
  /** People who finished onboarding — everything the UI labels "users" is this. */
  total_members?: number;
  /**
   * Every account we let in, including the ones still stuck in signup. Rows we
   * never approved are not accounts — they're in `never_approved`.
   */
  total_accounts?: number;
}

/**
 * A cohort is a group of PEOPLE, not a slice of the calendar: it is the batch
 * that arrived together, and you stay in the one you arrived with. The clock is
 * the day someone SIGNED UP, never their first appearance on the waitlist — a
 * waitlist row can predate the account by months. Boundaries are backend config
 * (`ADMIN_COHORT_BOUNDARIES`), so adding a cohort needs no frontend change (it
 * does need a backend deploy — the boundaries live in `config/settings.py`).
 */
export interface Cohort {
  n: number;
  label: string;
  start: string | null;
  end: string | null;
  /**
   * The subset of the batch who went on to make an account, which is why it is
   * smaller than the batch size the KPI strip divides by — this endpoint only
   * ever sees `profiles`. Someone we approved who never opened the app is in the
   * cohort with no account to count here.
   */
  members: number | null;
  /**
   * Our own accounts we approved in this window. Never part of `members` — a
   * teammate testing signup would otherwise BE the newest cohort here while the
   * funnel beside it, which has always dropped us, showed nobody.
   */
  internal?: number | null;
}

export interface CohortsResponse {
  cohorts: Cohort[];
  /**
   * member id -> cohort number, as the SERVER assigned it, off the day we
   * approved them. The dashboard rebuilds a few tiles in the browser from
   * unfiltered lists; they must file a member the same way the server-filtered
   * cards do. Neither side may fall back to the roster's own `created_at` —
   * that is usually a waitlist date, months off the approval.
   */
  assignments: Record<string, number>;
  /**
   * Our own QA accounts, so the browser-rebuilt tiles drop the same people the
   * server already dropped. `assignments` can't say it on its own: an id is
   * simply absent whether it's ours or just has no approval date, and the All
   * tab has to keep the second kind.
   */
  internal_ids?: string[];
  /** Accounts we never approved — in no cohort, so no tab can show them. */
  unassigned: number;
}

export async function getCohorts(): Promise<CohortsResponse> {
  const response = await fetch(`${BACKEND_API_URL}/admin/cohorts`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load cohorts');
  return response.json();
}

/**
 * `&cohorts=1,2` for every cohort-aware endpoint. Empty when every cohort (or
 * none) is picked, which is what makes "all tabs on" and "no tabs on" return
 * byte-identical numbers instead of differing by whoever lacks an approval date.
 */
export function cohortQ(cohorts: number[] | undefined, lead: '?' | '&' = '&'): string {
  return cohorts && cohorts.length ? `${lead}cohorts=${cohorts.join(',')}` : '';
}

/** All-time by design — see the endpoint's docstring. No window to pass. */
export async function getOnboardingMetrics(cohorts?: number[]): Promise<OnboardingMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/onboarding_metrics${cohortQ(cohorts, '?')}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load onboarding metrics');
  return response.json();
}

/**
 * Where booking attempts stop. Same shape of answer as the signup drop-off card:
 * one subject counted once, at the deepest stage it reached, so the bars sum to
 * the total and must never be subtracted from each other. The subject here is a
 * booking ATTEMPT, not a person — one member can appear in several bars.
 */
export interface BookingDropoff {
  configured: boolean;
  error?: string;
  /**
   * PostHog outage fallback — these attempts really happened, they're just as
   * of this timestamp rather than now. See `StaleBanner`.
   */
  stale_as_of?: string | null;
  window_days?: number;
  total_attempts?: number;
  unique_members?: number;
  booked?: number;
  /** Attempts whose events matched none of the known stages. */
  unstaged_attempts?: number;
  /**
   * Booking events from before 2026-06-10, when the attempt id was added to the
   * payload. They can't be stitched to an attempt, so they're reported here
   * rather than quietly dropped.
   */
  untracked_events?: number;
  /**
   * Our own runs, excluded unless asked for: anything tagged `staging`, plus
   * anything whose user id exists in no database (the April–June period when
   * the booking agent was being built against throwaway data).
   */
  test_attempts?: number;
  /** The subset of those that carried an explicit `staging` tag. */
  staging_attempts?: number;
  test_included?: boolean;
  funnel?: {
    stage: string;
    label: string;
    stopped: number;
    /** Members who stopped here, already resolved to names. */
    people: string[];
    /** Attempts here with no member attached. */
    anonymous: number;
    /** Why those are unnamed — two unrelated situations, not one. */
    unnamed_why?: {
      /** Had an account when they booked; it's gone now. */
      deleted_account: number;
      /** Booked from a signed-out device, so there's no user id to look up. */
      signed_out_device: number;
    };
    /** How to read the detail list, e.g. "asked for" / "failure". */
    detail_noun: string | null;
    detail: { label: string; count: number }[];
    studios: { name: string; count: number }[];
    is_final: boolean;
  }[];
  studios?: { name: string; count: number }[];
  /** The cohorts this reading is narrowed to, or null for all-time. */
  cohorts?: number[] | null;
  /**
   * Attempts another cohort owns — filed by their member, or by their own date
   * when nobody was signed in. Not missing, just on a different tab.
   */
  other_cohort_attempts?: number;
  /**
   * Attempts that belong to NO tab: no account we still hold, and no usable
   * date to file them under. Counted out loud rather than folded into whichever
   * cohort is on screen, which would make the tabs disagree with all-time.
   */
  unattributed_attempts?: number;
}

export async function getBookingDropoff(
  days = 90,
  includeTest = false,
  cohorts?: number[],
): Promise<BookingDropoff> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/bookings/dropoff?days=${days}&include_test=${includeTest}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load booking drop-off');
  return response.json();
}

/**
 * Every workout day we've generated, by type, and whether it was ticked off.
 * Headline numbers are DAYS (what a member marks complete); the type breakdown
 * is SESSIONS, because one day can hold a run and a lift.
 */
export interface WorkoutCompletionMetrics {
  members: number;
  seasons: number;
  total_days: number;
  rest_days: number;
  workout_days: number;
  /** Days with nothing a member could tick off (no exercises, no known label). */
  untrackable_days: number;
  past_workout_days: number;
  upcoming_workout_days: number;
  status_counts: {
    complete: number;
    partial: number;
    missed: number;
    /** Past, no flag either way. Both ways to log are live; ~96% still go unlogged. */
    unreported: number;
    upcoming: number;
  };
  /** Share of past days where the member told us anything at all. */
  logged_rate: number | null;
  /** Of the days that WERE logged, share done in full. */
  completion_rate: number | null;
  types: {
    type: string;
    label: string;
    category: string | null;
    sessions: number;
    complete: number;
    partial: number;
    missed: number;
    unreported: number;
    upcoming: number;
  }[];
  as_of: string;
}

export async function getWorkoutCompletionMetrics(
  cohorts?: number[],
): Promise<WorkoutCompletionMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/workouts/completion_metrics${cohortQ(cohorts, '?')}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load workout completion metrics');
  return response.json();
}

/** One headline number plus what it was a week ago (`prev: null` = unknown). */
export interface ExecMetric {
  label: string;
  value: number | null;
  prev: number | null;
  /** Small grey line under the number (e.g. "+41 founding (comped)"). */
  note?: string | null;
  unit?: string;
  /** Booking speed only: a DROP is the good direction. */
  lower_is_better?: boolean;
  /** Users only: people who finished onboarding. */
  onboarded?: number;
  /** Users only: the subscription half of the note, from our own mirror. */
  paying?: number;
  /** Users only: RevenueCat free trials. */
  trialing?: number;
  /** Users only: comped founding members, who never appear in RevenueCat. */
  founding?: number;
  /** Signed up only: how many of those rows made an in-app account. */
  accounts?: number;
  /** Signed up only: how many of those finished onboarding. */
  users?: number;
}

export interface ExecutiveSummary {
  as_of: string;
  window_days: number;
  metrics: {
    paying_users: ExecMetric;
    active_seasons: ExecMetric;
    total_accounts: ExecMetric;
    workouts_completed: ExecMetric;
    studio_bookings: ExecMetric;
    booking_speed: ExecMetric;
  };
  /**
   * 30 days, oldest first — the sparkline series behind the KPI strip.
   * Reconstructed from each row's own timestamps, because nothing keeps
   * history for these. Accounts and users are append-only and exact; live
   * plans and paying are a shape, not a ledger (a re-extended plan reads as
   * live on the days it lapsed, and churn is invisible).
   */
  trend?: { date: string; accounts: number; users: number; paying: number; live_plans: number }[];
}

/** The eight dashboard headline numbers, each with its week-ago comparison. */
export async function getExecutiveSummary(cohorts?: number[]): Promise<ExecutiveSummary> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/executive_summary${cohortQ(cohorts, '?')}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load executive summary');
  return response.json();
}

/** Full AI-generation breakdown for the /ai-generations drill-down (PostHog-sourced). */
export async function getAiGenerationsBreakdown(days = 30): Promise<AiGenerationsBreakdown> {
  const response = await fetch(`${BACKEND_API_URL}/admin/ai_generations/breakdown?days=${days}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load AI generations breakdown');
  return response.json();
}

/**
 * DAU/WAU as COMPUTED BY POSTHOG (source of truth) and merely displayed here.
 * Returns { configured: false } until POSTHOG_PERSONAL_API_KEY + POSTHOG_PROJECT_ID
 * are set on the backend.
 */
export async function getPostHogMetrics(
  days = 30,
  cohorts?: number[],
): Promise<PostHogMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/posthog/metrics?days=${days}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load PostHog metrics');
  return response.json();
}

/**
 * Shared poller for async generation workers (extend / build / MC V regen).
 *
 * The backend kicks these off as background workers (202) — generation
 * takes 7-8 minutes, beyond Railway's 5-minute request cap and Chrome's
 * ~300s fetch cap — so callers poll /admin/generation_status/{userId}
 * until the worker reaches a terminal state. Returns the terminal status
 * payload; throws on `failed` or timeout.
 */
const GEN_POLL_INTERVAL_MS = 5_000;
const GEN_POLL_TIMEOUT_MS = 20 * 60_000;

interface GenerationStatusPayload {
  status: string;
  error?: string;
  [key: string]: unknown;
}

async function pollGenerationUntilDone(
  userId: string,
  label: string,
): Promise<GenerationStatusPayload> {
  const deadline = Date.now() + GEN_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, GEN_POLL_INTERVAL_MS));

    const statusResponse = await fetch(
      `${BACKEND_API_URL}/admin/generation_status/${encodeURIComponent(userId)}`,
      { headers: await authHeaders() },
    );
    if (!statusResponse.ok) continue; // transient poll failure — keep waiting

    const status: GenerationStatusPayload = await statusResponse.json();
    if (status.status === 'failed') {
      throw new Error(status.error || `${label} failed`);
    }
    if (status.status === 'complete' || status.status === 'idle') {
      return status;
    }
    // status === 'running' / 'in_progress' — keep polling
  }
  throw new Error(`${label} timed out after 20 minutes`);
}

/**
 * Extend a user's workout season with the next 7-day week.
 * Kickoff (202) + poll + reload; callers keep the Promise<Season> contract.
 */
export async function extendSeason(userId: string): Promise<Season> {
  const response = await fetch(`${BACKEND_API_URL}/admin/extend_user_season/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: await authHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Failed to extend season');
  }

  await pollGenerationUntilDone(userId, 'Season extension');
  const season = await loadSeason(userId);
  if (!season) throw new Error('Extension finished but season could not be reloaded');
  return season;
}

// Magna Carta V regen — rebuild a user's season from scratch through the MC V
// agentic pipeline (in-loop hard rules + always-on reviewer are the quality
// gates) with a fail-closed pre-regen snapshot. Async: kickoff returns 202, the
// worker regenerates `weeks` weeks in the background, and this polls
// generation_status until terminal.

export interface RegenMagnaCartaResponse {
  response: string;
  user_id: string;
  snapshot_path: string;
  weeks_generated: number;
  season: Record<string, unknown>;
}

export async function regenSeasonMagnaCartaV(
  userId: string,
  weeks: number = 2,
): Promise<RegenMagnaCartaResponse> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/regen_season_magna_carta_v?user_id=${encodeURIComponent(userId)}&weeks=${weeks}`,
    { method: 'POST', headers: await authHeaders() },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = body.detail;
    const msg = typeof detail === 'string' ? detail : body.error || body.message || 'Regen failed';
    throw new Error(msg);
  }

  const terminal = await pollGenerationUntilDone(userId, 'MC V regen');
  const season = await loadSeason(userId);
  if (!season) throw new Error('Regen finished but season could not be reloaded');
  return {
    response: 'regen complete',
    user_id: userId,
    snapshot_path: String(body.snapshot_path ?? terminal.snapshot_path ?? ''),
    weeks_generated: Number(terminal.weeks_generated ?? 0),
    season: season as unknown as Record<string, unknown>,
  };
}

// Admin user deletion — cascade delete across every table referencing the
// user, then delete the auth.users row so email/phone is free to re-register.

export interface DeleteUserResponse {
  response: string;
  user_id: string;
  deleted_counts: Record<string, number>;
  auth_deleted: boolean;
  snapshot_path: string;
}

export async function deleteUser(userId: string): Promise<DeleteUserResponse> {
  const response = await fetch(`${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_user_id: userId }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = body.detail;
    const msg = typeof detail === 'string'
      ? detail
      : (detail && typeof detail === 'object' && 'message' in detail
        ? (detail as { message: string }).message
        : body.error || 'Delete failed');
    throw new Error(msg);
  }
  return response.json();
}

// Waitlist Management

/** A row from the `waitlist` table. phone_number is 11-digit (1 + 10). */
export interface WaitlistEntry {
  id: string;
  first_name?: string;
  last_name?: string;
  phone_number: string;
  email?: string;
  city?: string;
  referral?: string;
  tags?: string[];
  contact_status?: string;
  source?: string;
  status?: string; // 'pending' | 'admitted'
  created_at?: string;
}

/** A row from the `beta_access` allowlist. phone_number is the 11-digit PK. */
export interface BetaAccessEntry {
  phone_number: string;
  first_name?: string;
  last_name?: string;
  tags?: string[];
  contact_status?: string;
  allowed: boolean;
  /** Server-only. Written on beta_access; members cannot UPDATE that table. */
  capability_allowlist?: string[];
  created_at?: string;
}

/** Minimal member roster for reconcile. phone_number is raw 10-digit. */
export interface AccessMember {
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  subscription_plan?: string | null;
  subscription_status?: string | null;
}

export async function getWaitlist(limit = 1000): Promise<WaitlistEntry[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/waitlist?limit=${limit}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load waitlist');
  const data = await response.json();
  return data.waitlist ?? [];
}

export async function getBetaAccess(): Promise<BetaAccessEntry[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/beta_access`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load allowlist');
  const data = await response.json();
  return data.beta_access ?? [];
}

export async function getAccessMembers(): Promise<AccessMember[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/access/members`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load members');
  const data = await response.json();
  return data.members ?? [];
}

export interface PromoteResult {
  ok: boolean;
  phone_number: string;
  founding_member_granted: boolean;
}

export async function promoteBetaAccess(
  phoneNumber: string,
  firstName?: string,
  lastName?: string,
): Promise<PromoteResult> {
  const response = await fetch(`${BACKEND_API_URL}/admin/beta_access/promote`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      phone_number: phoneNumber,
      first_name: firstName ?? null,
      last_name: lastName ?? null,
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to promote');
  }
  return response.json();
}

// AI generation feedback (thumbs up/down + notes for post-learning).
// Backed by ai_generation_feedback table; one row per (user, admin, day).

export type FeedbackScope = 'day' | 'week' | 'season' | 'playground';
export type FeedbackRating = 'up' | 'down';

export interface FeedbackRow {
  id: string;
  user_id: string | null;        // null for scope='playground' (synthetic profile)
  admin_sub: string;
  generation_id: string | null;
  scope: FeedbackScope;
  workout_date: string | null;
  rating: FeedbackRating;
  notes: string | null;
  workout_snapshot: Record<string, unknown>;
  user_profile_snapshot: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SubmitFeedbackArgs {
  user_id?: string;               // required for scope='day'/'season', omitted for playground
  scope?: FeedbackScope;          // defaults to 'day' server-side
  workout_date?: string;          // ISO YYYY-MM-DD; required for scope='day'
  rating: FeedbackRating;
  notes?: string;
  generation_id?: string;
  workout_snapshot?: Record<string, unknown>;       // required for scope='playground'
  user_profile_snapshot?: Record<string, unknown>;  // optional override
}

export interface SubmitFeedbackResponse {
  feedback: FeedbackRow;
  created: boolean;
}

export interface ListFeedbackResponse {
  user_id: string;
  count: number;
  rows: FeedbackRow[];
}

export async function submitFeedback(args: SubmitFeedbackArgs): Promise<SubmitFeedbackResponse> {
  const response = await fetch(`${BACKEND_API_URL}/admin/validation/feedback`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to save feedback');
  }
  return response.json();
}

export async function revokeBetaAccess(phoneNumber: string): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/beta_access/revoke`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to revoke');
  }
}

// Unified user management (access source of truth + subscription)

export type SubscriptionPlan = 'founding_member' | 'monthly' | 'annual' | 'none';
/** Accepted by the backend; 'yearly' is a legacy alias normalized to 'annual'. */
export type SubscriptionPlanInput = SubscriptionPlan | 'yearly';

/** A merged-by-phone row across waitlist + beta_access + profiles. */
export interface UnifiedUser {
  phone_number: string; // 11-digit
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  city?: string | null;
  referral?: string | null;
  tags?: string[];
  access: boolean;            // beta_access.allowed — the single source of truth
  founding: boolean;          // founding member flag (free, bypasses paywall)
  in_waitlist: boolean;
  in_allowlist: boolean;
  is_member: boolean;
  internal?: boolean;         // our own QA bot — shown in the table, left out of the counts
  user_id?: string | null;    // profile id (members only)
  subscription_plan?: string | null;
  subscription_status?: string | null;
  waitlist_status?: string | null;
  member_reason?: string | null;
  // FOUR different dates, never collapsed into one: first appearance anywhere
  // (usually the waitlist signup), the day we gave them access, the day they made
  // an account, and the day they finished onboarding. `approved_at` is the cohort
  // clock — see shared.approval_dates; the others can each be months away from it.
  created_at?: string | null;
  approved_at?: string | null;
  account_created_at?: string | null;
  onboarded_at?: string | null;
}

export async function getUnifiedUsers(): Promise<UnifiedUser[]> {
  // /admin/access/users, NOT /admin/users — admin.py registers GET /admin/users
  // first, so it would shadow the unified route.
  const response = await fetch(`${BACKEND_API_URL}/admin/access/users`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load users');
  const data = await response.json();
  return data.users ?? [];
}

export interface AdminNote {
  note: string;
  updated_by?: string | null;
  updated_at?: string | null;
}

/** All shared admin notes, keyed by 11-digit phone (Users & Access row key). */
export async function getAdminNotes(): Promise<Record<string, AdminNote>> {
  const response = await fetch(`${BACKEND_API_URL}/admin/access/notes`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load notes');
  const data = await response.json();
  return data.notes ?? {};
}

/** Upsert the shared admin note for a phone; an empty note deletes it.
 * author is the admin's email, recorded as updated_by (display-only). */
export async function setAdminNote(phoneNumber: string, note: string, author?: string): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/access/notes/set`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber, note, author: author ?? null }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to save note');
  }
}

/** Set the Access flag (beta_access.allowed) for a phone. The app gate. */
export async function setAccess(
  phoneNumber: string,
  allowed: boolean,
  firstName?: string,
  lastName?: string,
): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/access/set`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      phone_number: phoneNumber,
      allowed,
      first_name: firstName ?? null,
      last_name: lastName ?? null,
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to set access');
  }
}

/** Toggle the founding-member flag (free, bypasses paywall). Writes the
 * per-phone beta_access.founding flag and, for members, flips their profile
 * plan immediately. Decoupled from monthly/yearly (those come from RevenueCat). */
export async function setFounding(
  phoneNumber: string,
  founding: boolean,
  userId?: string | null,
): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/founding/set`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber, founding, user_id: userId ?? null }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to set founding');
  }
}

/** Set a member's subscription plan (profiles). Decoupled from access. */
export async function setSubscription(userId: string, plan: SubscriptionPlanInput): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/subscription/set`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ user_id: userId, plan }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to set subscription');
  }
}

export interface NewUserInput {
  first_name: string;
  phone_number: string;
  last_name?: string;
  email?: string;
  city?: string;
  referral?: string;
  tags?: string[];
}

export async function addUser(user: NewUserInput): Promise<void> {
  const response = await fetch(`${BACKEND_API_URL}/admin/users/add`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(user),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to add user');
  }
}

export async function addUsersBulk(users: NewUserInput[]): Promise<{ added: number; skipped: number; already_listed?: number; merged?: number }> {
  const response = await fetch(`${BACKEND_API_URL}/admin/users/add_bulk`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ users }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to add users');
  }
  return response.json();
}

export async function listFeedback(userId: string, limit = 200): Promise<ListFeedbackResponse> {
  const url = new URL(`${BACKEND_API_URL}/admin/validation/feedback`);
  url.searchParams.set('user_id', userId);
  url.searchParams.set('limit', String(limit));
  const response = await fetch(url.toString(), { headers: await authHeaders() });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load feedback');
  }
  return response.json();
}

export async function deleteFeedback(feedbackId: string): Promise<{ response: string; id: string }> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/validation/feedback/${encodeURIComponent(feedbackId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to delete feedback');
  }
  return response.json();
}

// Archetype get/set/clear — admin override on profiles.agent_overrides.archetype.
// Reading runs classify_archetype on the live profile and reports both the
// computed value and whatever override is in place.

export interface ArchetypeState {
  user_id: string;
  archetype: string;             // effective value (override wins if set)
  computed_archetype: string;    // what classifier would say with no override
  override: string | null;       // raw override value if set
  source: 'override' | 'computed';
  rule_matched: string;
  confidence: 'high' | 'medium' | 'low';
  valid_archetypes: string[];    // populates the UI dropdown
}

export async function getArchetype(userId: string): Promise<ArchetypeState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/archetype`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load archetype');
  }
  return response.json();
}

export async function setArchetype(userId: string, archetype: string): Promise<ArchetypeState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/archetype`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ archetype }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to set archetype');
  }
  return response.json();
}

export async function clearArchetype(userId: string): Promise<ArchetypeState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/archetype`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to clear archetype');
  }
  return response.json();
}

export interface DeleteUserResult {
  account_deleted: boolean;
  waitlist_deleted: number;
  beta_access_deleted: number;
}

/** Delete a user's data (for clearing test users): purges waitlist + allowlist
 * rows for the phone, and cascade-deletes the full account when userId is given. */
export async function deleteUnifiedUser(phoneNumber: string, userId?: string | null): Promise<DeleteUserResult> {
  const response = await fetch(`${BACKEND_API_URL}/admin/access/delete_user`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber, user_id: userId ?? null }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.detail || 'Failed to delete user');
  }
  return response.json();
}

// Admin goal management: CRUD any user's goals via the admin endpoints. Backed
// by goal_service, so the same caps / last-standard / event-date validation
// applies; origin is set server-side to "admin".

export interface AdminGoal {
  id: string;
  goal_type: 'standard' | 'event';
  label: string;
  target_date: string | null;
  weeks_to_target: number | null;
  days_to_target: number | null;
  origin: string;
}

function goalErr(body: { detail?: string; error?: string }, fallback: string): string {
  const code = body.error || body.detail || '';
  const map: Record<string, string> = {
    goal_cap_reached: 'Goal limit reached (max 2 of that type).',
    last_standard_goal: 'Cannot remove the only standard goal — edit it instead.',
    event_goal_requires_date: 'An event goal requires a target date.',
    goal_not_found: 'Goal not found.',
  };
  return map[code] || code || fallback;
}

export async function listUserGoals(userId: string): Promise<AdminGoal[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/users/${encodeURIComponent(userId)}/goals`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch goals');
  }
  const data = await response.json();
  return data.goals ?? [];
}

export async function createUserGoal(
  userId: string,
  goal: { goal_type: 'standard' | 'event'; label: string; target_date: string | null },
): Promise<AdminGoal> {
  const response = await fetch(`${BACKEND_API_URL}/admin/users/${encodeURIComponent(userId)}/goals`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(goal),
  });
  if (!response.ok) {
    throw new Error(goalErr(await response.json().catch(() => ({})), 'Failed to create goal'));
  }
  return (await response.json()).goal;
}

export async function updateUserGoal(
  userId: string,
  goalId: string,
  patch: { label?: string; target_date?: string | null },
): Promise<AdminGoal> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/users/${encodeURIComponent(userId)}/goals/${encodeURIComponent(goalId)}`,
    { method: 'PATCH', headers: await authHeaders(), body: JSON.stringify(patch) },
  );
  if (!response.ok) {
    throw new Error(goalErr(await response.json().catch(() => ({})), 'Failed to update goal'));
  }
  return (await response.json()).goal;
}

export async function deleteUserGoal(userId: string, goalId: string): Promise<void> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/users/${encodeURIComponent(userId)}/goals/${encodeURIComponent(goalId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!response.ok) {
    throw new Error(goalErr(await response.json().catch(() => ({})), 'Failed to delete goal'));
  }
}

// Diagnosis observability: digest endpoint (GET /admin/diagnosis/digest) and
// the per-user steering knob (GET/POST/DELETE /admin/user/{id}/diagnosis_steering).

export type DiagnosisPosture =
  | 'progress' | 'hold' | 'deload' | 'rebuild' | 'taper' | 'build-foundation';

export interface DiagnosisRow {
  id: string;
  user_id: string;
  generated_at: string;
  posture: DiagnosisPosture | null;
  primary_driver: string | null;
  cited_inputs: string[];          // ["key:value", ...]
  confidence: 'high' | 'medium' | 'low' | null;
  steering_active: boolean;
  generation_outcome: string;      // success | failed | failed_no_diagnosis
}

export interface InvariantViolation {
  user_id: string;
  prev_id: string | null;
  curr_id: string | null;
  prev_posture: string | null;
  curr_posture: string | null;
  generated_at: string | null;
}

export interface PostureChurn {
  user_id: string;
  iso_week: string;
  changes: number;
}

export interface DiagnosisDigest {
  range: { start: string | null; end: string | null };
  count: number;
  truncated: boolean;
  rows: DiagnosisRow[];
  invariant_violations: InvariantViolation[];
  posture_churn: PostureChurn[];
}

export interface DigestFilters {
  user_id?: string;
  start?: string;     // ISO date YYYY-MM-DD
  end?: string;       // ISO date YYYY-MM-DD
  posture?: string;
}

export async function fetchDiagnosisDigest(filters: DigestFilters = {}): Promise<DiagnosisDigest> {
  const params = new URLSearchParams();
  if (filters.user_id) params.set('user_id', filters.user_id);
  if (filters.start) params.set('start', filters.start);
  if (filters.end) params.set('end', filters.end);
  if (filters.posture) params.set('posture', filters.posture);
  const qs = params.toString();
  const response = await fetch(
    `${BACKEND_API_URL}/admin/diagnosis/digest${qs ? `?${qs}` : ''}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.detail || 'Failed to load diagnosis digest');
  }
  return response.json();
}

export interface SteeringState {
  user_id: string;
  steering_active: boolean;        // RESOLVED effective state
  override: boolean | string | null;
  env_default: boolean;
  source: 'override' | 'env_default';
}

export async function getDiagnosisSteering(userId: string): Promise<SteeringState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/diagnosis_steering`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load steering state');
  }
  return response.json();
}

export async function setDiagnosisSteering(userId: string, enabled: boolean): Promise<SteeringState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/diagnosis_steering`,
    { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ enabled }) },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to set steering state');
  }
  return response.json();
}

export async function clearDiagnosisSteering(userId: string): Promise<SteeringState> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/user/${encodeURIComponent(userId)}/diagnosis_steering`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to clear steering state');
  }
  return response.json();
}

// Studios

/**
 * A row in the Supabase `studios` table — the source of truth for which
 * studios the booking agent can book at (also read by the mobile app).
 */
export interface Studio {
  id: string;
  name: string;
  activity_type: string;
  is_active: boolean;
  display_order: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  city: string | null;
  url: string | null;
  image_url: string | null;
  location_pill: string | null;
  address: string | null;
  logo_url: string | null;
  booking_method: string;
  booking_system: string;
  venue_id: string | null;
  is_partner: boolean;
  /** Partnership tier. Admin-only metadata; `is_partner` is derived from it
   *  server-side. 'none' = not a partner. */
  partner_tier: PartnerTier;
  priority: string;
  // API-based booking (in addition to the agentic flow). Tracking/routing
  // fields; nothing routes on these until api_booking_enabled is flipped true.
  // api_provider routing keys: 'mariana_tek' | 'momence' | 'mindbody' | 'wodify'
  // (null = agentic only). api_credential_ref holds the NAME of the env var /
  // secret key for the provider credential, never the secret value.
  api_provider: string | null;
  api_location_id: string | null;
  api_credential_ref: string | null;
  api_booking_enabled: boolean;
  api_metadata: Record<string, unknown>;
  /** Neighborhood label (class-catalog field). Often mirrors location_pill but
   *  is the canonical field the scraper/recon tooling writes. */
  neighborhood: string | null;
  /** IANA tz, e.g. "America/Los_Angeles". Pins the booking agent's headless
   *  browser so class times render in the studio's local time, not the
   *  cloud proxy's. DERIVED FROM `city` BY THE DATABASE - a trigger
   *  (backend/sql/studios_timezone_trigger.sql) sets it on every insert and on
   *  any city change, so the admin form shows it read-only rather than letting
   *  it drift out of step with the city. */
  timezone: string;
  /** READ-ONLY (scraper-owned). Config the scraper auto-derives + PATCHes when
   *  it onboards a newly-added studio. Empty/null = not yet configured
   *  (auto-onboard pending on the next scrape). `platform` (when present) names
   *  the detected booking platform. Never sent on create/update (see
   *  StudioInput). */
  scrape_config?: Record<string, unknown> | null;
  /** READ-ONLY (scraper-owned). Class ingestion mode the scraper sets:
   *  'catalog' | 'login_required' | 'appointment' | 'live_discovery'. Never
   *  sent on create/update. */
  class_source_mode?: string | null;
  // --- GTM-owned studio relationship contacts (all optional) ---------------
  // Added for the 17 Aug studio-dashboard request. Never read by the booking
  // agent or the mobile app; these exist so studio contacts live on the studio
  // record instead of a side spreadsheet.
  // SQL: backend/sql/studios_contact_columns.sql
  primary_poc?: string | null;
  primary_email?: string | null;
  other_email?: string | null;
  secondary_poc?: string | null;
  secondary_email?: string | null;
  tertiary_poc?: string | null;
  tertiary_email?: string | null;
  phone_number?: string | null;
  comment?: string | null;
  // --- GTM-owned API integration + social status (all optional) ------------
  // Added for the 31 Aug studio-dashboard PRD. Same contract as the contacts
  // above: admin-dashboard only, never read by the booking agent.
  // `api_key` is write-only on create/update; GET responses expose `api_key_set`.
  // A live credential belongs in `api_credential_ref` (env var NAME).
  // SQL: backend/sql/studios_api_social_columns.sql
  api_status?: string | null;
  /** True when a non-empty api_key is stored. The value is never returned. */
  api_key_set?: boolean;
  social_media_status?: string | null;
}

export type PartnerTier = 'founding' | 'non_founding' | 'none';

/** Fields the admin form can write. `id`/timestamps are server-managed;
 *  `scrape_config`/`class_source_mode` are scraper-owned READ-ONLY fields the
 *  admin must never send (the backend ignores them on write). Omitted here so
 *  they can never leak into a create/update payload. */
export type StudioInput = Omit<
  Studio,
  'id' | 'created_at' | 'updated_at' | 'scrape_config' | 'class_source_mode' | 'api_key_set'
> & {
  /** Write-only partner reference. Omitted on PATCH to keep the stored value. */
  api_key?: string | null;
};

export async function getStudios(): Promise<Studio[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studios`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studios');
  }
  const data = await response.json();
  return data.studios as Studio[];
}

export async function createStudio(studio: Partial<StudioInput>): Promise<Studio> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studios`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(studio),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to create studio');
  }
  const data = await response.json();
  return data.studio as Studio;
}

export async function updateStudio(
  id: string,
  updates: Partial<StudioInput>,
): Promise<Studio> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/studios/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify(updates),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to update studio');
  }
  const data = await response.json();
  return data.studio as Studio;
}

export async function deleteStudio(id: string): Promise<void> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/studios/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    // 409 studio_has_bookings sends detail as {error, message, booking_count}
    // rather than a plain string — surface the human-readable message.
    const detailMessage =
      typeof err.detail === 'string' ? err.detail : err.detail?.message;
    throw new Error(detailMessage || err.error || 'Failed to delete studio');
  }
}

export interface StudioImportChange {
  from: string;
  to: string;
}

export interface StudioImportRow {
  sheet_name: string;
  studio_id?: string;
  studio_name?: string;
  match?: string;
  reason?: string;
  changes?: Record<string, StudioImportChange>;
  /** Set only on `creates`: the columns the new studio would be added with. */
  values?: Record<string, string | boolean>;
}

export interface StudioImportResult {
  updates: StudioImportRow[];
  unchanged: StudioImportRow[];
  /** Studios the sheet would ADD (inactive) because nothing in the table
   * resembles the name. A near-miss is flagged instead, never added. */
  creates: StudioImportRow[];
  flagged: StudioImportRow[];
  applied: boolean;
  written: number;
  created: number;
  columns: string[];
  studio_count: number;
  failed?: { studio_name: string; error: string }[];
}

/** Merge a parsed GTM spreadsheet into the studios table.
 *
 * Updates the studios it matches and ADDS (inactive) the ones whose names
 * resemble nothing in the table. Call once with apply=false to preview (nothing
 * is written), then again with the same rows and apply=true once a human has
 * read the diff. The CSV is parsed in the browser — no file is uploaded. */
export async function importStudios(
  rows: Record<string, string>[],
  apply: boolean,
): Promise<StudioImportResult> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studios/import`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ rows, apply }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detailMessage =
      typeof err.detail === 'string' ? err.detail : err.detail?.[0]?.msg;
    throw new Error(detailMessage || err.error || 'Failed to import studios');
  }
  return (await response.json()) as StudioImportResult;
}

// Program Integrity (MC VI structural validator sweep)

export interface IntegrityFinding {
  rule: string;
  date: string | null;
  emphasis?: string;
  missing_patterns?: string[];
  detail: string;
}

export interface IntegrityUser {
  user_id: string;
  name: string;
  archetype: string;
  declared_strength_frequency: number;
  strength_sessions: number;
  scored_sessions: number;
  finding_count: number;
  findings: IntegrityFinding[];
}

export interface ProgramIntegrityReport {
  summary: {
    users_checked: number;
    users_with_findings: number;
    users_with_findings_pct: number;
    scored_strength_sessions: number;
    total_strength_sessions: number;
    antagonist_violation_pct: number;
    findings_by_rule: Record<string, number>;
  };
  by_archetype: Record<
    string,
    { scored_sessions: number; antagonist_violations: number }
  >;
  users: IntegrityUser[];
}

export async function getProgramIntegrity(): Promise<ProgramIntegrityReport> {
  const response = await fetch(`${BACKEND_API_URL}/admin/program_integrity`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load program integrity report');
  }
  return response.json();
}

// Studio Catalog Health (class-catalog scraper coverage)
// Read-only observability for the class-catalog scraper: how many studios are
// configured, how many have fresh classes, deep-link coverage, playbook
// coverage, per-platform/tier/mode breakdowns, and the recent scraper cron
// runs. Admin-gated.

/** Class-source mode for a studio. `catalog` = fully scrapable schedule,
 *  `login_required` = needs an auth'd session, `live_discovery` = discovered at
 *  book time, `appointment` = appointment-only (no fixed class list). */
export type ClassSourceMode =
  | 'catalog'
  | 'login_required'
  | 'live_discovery'
  | 'appointment';

export interface StudioCatalogOverall {
  studios_total: number;
  studios_active: number;
  studios_inactive: number;
  configured: number;
  unconfigured: number;
  studios_with_classes: number;
  classes_total: number;
  deep_link_pct: number;
  fresh_pct: number;
  playbooks_total: number;
  studios_with_playbook: number;
}

export interface StudioCatalogPlatformRow {
  platform: string;
  studios: number;
  classes: number;
}

export interface StudioCatalogTierRow {
  partner_tier: string;
  active: number;
  configured: number;
  with_classes: number;
}

export interface StudioCatalogSourceModeRow {
  mode: string; // ClassSourceMode, but tolerate unknown modes from the backend
  active_studios: number;
}

export interface StudioCatalogAttention {
  configured_no_classes: string[];
  unconfigured_partners: string[];
  stale_over_24h: string[];
  missing_deep_links: string[];
}

export interface StudioCatalogCronRun {
  executed_at: string;
  status: string;
  users_processed: number;
  users_succeeded: number;
  users_failed: number;
  duration_seconds: number;
  errors: string[];
}

/** One studio's scraper coverage row. Superset of the editable `Studio` shape
 *  plus the catalog-derived counts (class_count, deep-link/fresh coverage). */
export interface StudioCatalogStudio {
  id: string;
  name: string;
  activity_type: string;
  is_active: boolean;
  city: string | null;
  location_pill: string | null;
  neighborhood: string | null;
  address: string | null;
  timezone: string | null;
  external_id: string | null;
  booking_method: string | null;
  booking_system: string | null;
  is_partner: boolean;
  partner_tier: string;
  priority: string | null;
  class_source_mode: string;
  scrape_config: Record<string, unknown>;
  configured: boolean;
  platform: string | null;
  has_playbook: boolean;
  playbook_file: string | null;
  class_count: number;
  days_covered: number;
  first_day: string | null;
  last_day: string | null;
  deep_link_count: number;
  deep_link_pct: number;
  fresh_count: number;
  scheduled_count: number;
  full_count: number;
  cancelled_count: number;
  last_seen_at: string | null;
}

export interface StudioCatalogHealth {
  generated_at: string;
  overall: StudioCatalogOverall;
  by_platform: StudioCatalogPlatformRow[];
  by_tier: StudioCatalogTierRow[];
  by_source_mode: StudioCatalogSourceModeRow[];
  attention: StudioCatalogAttention;
  cron_runs: StudioCatalogCronRun[];
  studios: StudioCatalogStudio[];
}

/**
 * Class-catalog scraper coverage/health snapshot. Admin-gated; mirrors the
 * auth + error handling of the other admin getters (getStudios).
 */
export async function getStudioCatalogHealth(): Promise<StudioCatalogHealth> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studio-catalog/health`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studio catalog health');
  }
  return response.json();
}

// Studio classes drawer (per-studio detail)

/** Every studio column, as returned by the detail endpoint's `studio` field.
 *  Superset of the health row: adds url / display_order / created_at / updated_at. */
export interface StudioDetailRow {
  id: string;
  name: string;
  activity_type: string | null;
  is_active: boolean;
  display_order: number | null;
  city: string | null;
  url: string | null;
  location_pill: string | null;
  neighborhood: string | null;
  address: string | null;
  timezone: string | null;
  external_id: string | null;
  booking_method: string | null;
  booking_system: string | null;
  is_partner: boolean;
  partner_tier: string;
  priority: string | null;
  class_source_mode: string;
  scrape_config: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

/** One row from `studio_classes` (every column). Fields may be null/empty -
 *  muscle_groups / exercise_list are surfaced verbatim even when empty. */
export interface StudioClassRow {
  id: string;
  external_key: string | null;
  source: string | null;
  name: string | null;
  category: string | null;
  instructor: string | null;
  class_date: string;
  start_time: string | null; // HH:MM:SS
  starts_at: string | null; // ISO
  timezone: string | null;
  duration_min: number | null;
  muscle_groups: string[] | null;
  exercise_list: string[] | null;
  other_details: string | null;
  booking_url: string | null;
  status: string; // "scheduled" | "full" | "cancelled"
  spots_available: number | null;
  availability?: string; // "open" | "waitlist" | "full" | "unknown" (live, per scrape)
  waitlist_available?: boolean;
  metadata: Record<string, unknown> | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

export interface StudioClassesDetail {
  studio: StudioDetailRow | null;
  playbook_file: string | null;
  class_count: number;
  days: string[]; // ISO dates, sorted
  by_day: Record<string, StudioClassRow[]>;
  classes: StudioClassRow[]; // sorted by class_date, start_time
}

/**
 * Full detail for one studio: every studio column + all upcoming class rows
 * (every field, incl. muscle_groups / exercise_list / booking_url) grouped by
 * day. Admin-gated; mirrors the auth + error handling of getStudioCatalogHealth.
 */
export async function getStudioClassesDetail(studioId: string): Promise<StudioClassesDetail> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/studio-catalog/studios/${encodeURIComponent(studioId)}/classes`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studio classes');
  }
  return response.json();
}

// Studio Information (site-crawler raw data)

/** One heading harvested from a crawled page. */
export interface StudioSitePageHeading {
  tag: string; // "h1" | "h2" | ...
  text: string;
}

/** The most recent crawl summary attached to a studio in the overview. Null
 *  for never-crawled studios (they still appear so coverage is visible). */
export interface StudioSiteLastCrawl {
  id: string;
  status: string; // running | succeeded | failed | capped | skipped_aggregator
  pages_crawled: number;
  walls_hit: number;
  pages_capped: boolean;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** One studio row in the overview: seed url + origin + crawl stats. */
export interface StudioSiteOverviewStudio {
  id: string;
  name: string;
  activity_type: string | null;
  is_active: boolean;
  url: string | null;
  origin: string | null;
  last_crawl: StudioSiteLastCrawl | null;
  page_count: number;
  screenshot_count: number;
}

/** Roll-up totals across every studio. */
export interface StudioSiteOverall {
  studios_total: number;
  studios_with_url: number;
  studios_crawled: number;
  pages_total: number;
  walls_total: number;
  screenshots_total: number;
  last_crawl_at: string | null;
}

export interface StudioSiteOverview {
  generated_at: string;
  provisioned: boolean; // false before the migration exists -> empty studios
  overall: StudioSiteOverall;
  studios: StudioSiteOverviewStudio[];
}

/** One crawl history row (last 5 per studio, newest first). */
export interface StudioSiteCrawlRow {
  id: string;
  status: string;
  pages_crawled: number;
  walls_hit: number;
  pages_capped: boolean;
  max_pages: number | null;
  max_depth: number | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** One crawled page as listed in the per-studio detail (list payload:
 *  raw_html + full links excluded; text_preview + text_length only). */
export interface StudioSitePageRow {
  id: string;
  url: string;
  depth: number;
  http_status: number | null;
  title: string | null;
  headings: StudioSitePageHeading[] | null;
  text_preview: string | null;
  text_length: number;
  link_count: number;
  is_wall: boolean;
  wall_reason: string | null; // login_form | login_url | aggregator_seed
  screenshot_url: string | null; // pre-signed (1h TTL) or null
  content_hash: string | null;
  error: string | null;
  last_crawled_at: string | null;
}

/** The studio header returned by the per-studio pages endpoint. */
export interface StudioSitePagesStudio {
  id: string;
  name: string;
  url: string | null;
  origin: string | null;
}

export interface StudioSitePagesDetail {
  studio: StudioSitePagesStudio;
  crawls: StudioSiteCrawlRow[]; // last 5, newest first
  page_count: number;
  pages: StudioSitePageRow[]; // ordered by (depth, url)
}

/** Full single-page payload: full text_content + links (+ raw_html when asked). */
export interface StudioSitePageFull {
  id: string;
  studio_id: string;
  url: string;
  depth: number;
  http_status: number | null;
  content_type: string | null;
  title: string | null;
  headings: StudioSitePageHeading[] | null;
  text_content: string | null;
  aria_snapshot: string | null;
  links: string[] | null;
  raw_html: string | null;
  is_wall: boolean;
  wall_reason: string | null;
  screenshot_url: string | null;
  first_crawled_at: string | null;
  last_crawled_at: string | null;
}

/**
 * Site-crawler coverage overview: per-studio crawl stats + roll-up totals.
 * Admin-gated; mirrors the auth + error handling of getStudioCatalogHealth.
 * Returns `provisioned:false` + empty studios before the migration/crawl exist.
 */
export async function getStudioSiteOverview(): Promise<StudioSiteOverview> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studio-info/overview`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studio site overview');
  }
  return response.json();
}

/**
 * Per-studio crawled pages + crawl history. `raw_html` and full `links` are
 * excluded here (see getStudioSitePage for those). Admin-gated.
 */
export async function getStudioSitePages(studioId: string): Promise<StudioSitePagesDetail> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/studio-info/studios/${encodeURIComponent(studioId)}/pages`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studio site pages');
  }
  return response.json();
}

/**
 * Full detail for one crawled page (full text_content + links; raw_html only
 * when includeHtml is true AND it was stored). Fetched on demand when a page's
 * text preview is expanded. Admin-gated.
 */
export async function getStudioSitePage(
  pageId: string,
  includeHtml = false,
): Promise<StudioSitePageFull> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/studio-info/pages/${encodeURIComponent(pageId)}?include_html=${includeHtml}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load studio site page');
  }
  const data = await response.json();
  return data.page as StudioSitePageFull;
}

/**
 * Scraped class-catalog rollup (backend/api/routers/admin_studio_health.py
 * :class_catalog). One row per unique (studio, class name), deduped across
 * every scraped date/time instance in `studio_classes`, with studio_id
 * already resolved to studio_name. `summary` reports what fraction of
 * classes have each field populated — muscle_groups/exercise_list are
 * currently 0% (scraper doesn't fill them yet).
 */
export interface ClassCatalogEntry {
  studio_id: string;
  studio_name: string;
  studio_activity_type: string | null;
  studio_city: string | null;
  studio_active: boolean;
  name: string;
  category: string | null;
  instructors: string[];
  duration_min: number | null;
  muscle_groups: string[];
  exercise_list: string[];
  other_details: string | null;
  booking_url: string | null;
  platform: string | null;
  instance_count: number;
  next_date: string | null;
  last_seen_at: string | null;
}

export interface ClassCatalogSummary {
  total_instances: number;
  total_unique_classes: number;
  studios_covered: number;
  with_category: number;
  pct_category: number | null;
  with_instructor: number;
  pct_instructor: number | null;
  with_duration: number;
  pct_duration: number | null;
  with_muscle_groups: number;
  pct_muscle_groups: number | null;
  with_exercise_list: number;
  pct_exercise_list: number | null;
  with_other_details: number;
  pct_other_details: number | null;
  with_booking_url: number;
  pct_booking_url: number | null;
}

export interface ClassCatalogResponse {
  generated_at: string;
  summary: ClassCatalogSummary;
  classes: ClassCatalogEntry[];
}

export async function getClassCatalog(): Promise<ClassCatalogResponse> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studio-catalog/class-catalog`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load class catalog');
  }
  return response.json();
}

/** Editable subset of a class-catalog group. instructor/booking_url/platform
 *  are deliberately excluded server-side (see admin_studio_health.py) — they
 *  legitimately vary per scraped instance, so a bulk edit would overwrite
 *  real per-occurrence data rather than enrich it. */
export interface ClassCatalogFieldsInput {
  name?: string;
  category?: string | null;
  duration_min?: number | null;
  muscle_groups?: string[];
  exercise_list?: string[];
  other_details?: string | null;
}

/**
 * Writes the given fields to EVERY studio_classes row in this (studio, class
 * name) group — every scraped date/time instance gets the same value, since
 * these fields describe what kind of class it is, not the specific
 * occurrence. Returns the recomputed group so the table can update in place.
 */
export async function updateClassCatalogEntry(
  studioId: string,
  name: string,
  updates: ClassCatalogFieldsInput,
): Promise<ClassCatalogEntry> {
  const response = await fetch(`${BACKEND_API_URL}/admin/studio-catalog/class-catalog`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ studio_id: studioId, name, updates }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to update class');
  }
  const data = await response.json();
  return data.class as ClassCatalogEntry;
}

// Chat transcripts (read-only)
//
// PostHog carries only `message_length` for a chat turn, never the text, so
// these read the transcripts through the read-only admin_chat_viewer endpoints.

export interface ChatTranscriptSummary {
  id: string;
  user_id: string | null;
  type: string | null;
  title: string | null;
  archived_at: string | null;
  last_message_at: string | null;
  created_at: string | null;
  /** The best label the backend holds for them: their profile name, else the
   *  name on the waitlist/allowlist row with the same phone, else the phone
   *  number itself. Null only when we hold none of the three. */
  name: string | null;
  message_count: number;
  user_message_count: number;
  opener: string | null;
  opener_truncated: boolean;
  opener_at: string | null;
  /** When the MEMBER last wrote. Null on a thread only the agent has spoken in.
   *  This orders the list — `last_message_at` moves when we reach out, which
   *  floats unanswered outreach above people who are actually replying. */
  last_user_message_at: string | null;
  /** Newest message from either side, for the list preview. */
  last_message: string | null;
  last_message_truncated: boolean;
  last_message_role: string | null;
}

export interface ChatTranscriptMessage {
  id: string;
  role: string;
  content: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export type ChatTranscriptOutboundStatus = 'sent' | 'held' | 'failed' | 'unconfirmed';

export interface ChatTranscriptOutbound {
  id: string;
  kind: 'parts' | 'poll' | 'reaction' | 'contact_card';
  send_class: string | null;
  status: ChatTranscriptOutboundStatus;
  hold_reason: string | null;
  parts: { type: string; value?: string; url?: string; attachment_id?: string }[];
  detail: Record<string, unknown>;
  idempotency_key: string | null;
  provider_message_id: string | null;
  source_message_id: string | null;
  sent_at: string;
}

export interface ChatTranscriptDetail {
  session: ChatTranscriptSummary;
  messages: ChatTranscriptMessage[];
  outbound?: ChatTranscriptOutbound[];
  outbound_available?: boolean;
  read_only: boolean;
}

export async function fetchChatTranscripts(opts: {
  days?: number;
  limit?: number;
  offset?: number;
  user_id?: string;
} = {}): Promise<{ sessions: ChatTranscriptSummary[]; total: number; days: number }> {
  const params = new URLSearchParams();
  if (opts.days) params.set('days', String(opts.days));
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  if (opts.user_id) params.set('user_id', opts.user_id);
  const qs = params.toString();
  const response = await fetch(
    `${BACKEND_API_URL}/admin/chat_transcripts${qs ? `?${qs}` : ''}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.detail || 'Failed to load chat transcripts');
  }
  return response.json();
}

export async function fetchChatTranscript(sessionId: string): Promise<ChatTranscriptDetail> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/chat_transcripts/${encodeURIComponent(sessionId)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.detail || 'Failed to load transcript');
  }
  return response.json();
}

export type MemberMessageBlock = 'no_profile' | 'no_first_name' | 'no_imessage' | 'lookup_failed';

export type MemberMessageOutcome =
  | Exclude<MemberMessageBlock, 'lookup_failed'>
  | 'pending' | 'sent' | 'uncertain' | 'stopped' | 'held'
  | 'not_delivered' | 'failed' | 'waiting' | 'unknown';

export interface MemberMessageBatch {
  user_ids: string[];
  messages: string[];
  batch_id: string;
}

export interface MemberMessagePreview {
  user_id: string;
  name: string | null;
  bubbles: string[];
  blocked: MemberMessageBlock | null;
}

export interface MemberMessageStatus {
  results: { user_id: string; outcome: MemberMessageOutcome }[];
}

const MEMBER_MESSAGE_ERRORS: Record<string, string> = {
  linq_not_configured: "Texting isn't set up on this server, so nothing was sent.",
  empty_message: 'Every message needs some text.',
  internal_error: 'The server hit an error. Nothing new was sent; try again.',
};

async function postMemberMessages(path: string, batch: MemberMessageBatch): Promise<Response> {
  const response = await fetch(`${BACKEND_API_URL}/admin/member_messages/${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(batch),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const code = typeof err.error === 'string' ? err.error : typeof err.detail === 'string' ? err.detail : '';
    throw new Error(MEMBER_MESSAGE_ERRORS[code] ?? `Request failed (${response.status})`);
  }
  return response;
}

export async function previewMemberMessages(batch: MemberMessageBatch): Promise<MemberMessagePreview[]> {
  const data = await (await postMemberMessages('preview', batch)).json();
  return data.results;
}

export async function sendMemberMessages(batch: MemberMessageBatch): Promise<void> {
  await postMemberMessages('send', batch);
}

export async function fetchMemberMessageStatus(batch: MemberMessageBatch): Promise<MemberMessageStatus> {
  return (await postMemberMessages('status', batch)).json();
}

/** One person who has texted us, and how far they got. */
export interface ImessagePerson {
  user_id: string;
  /** Profile name, else the name on the waitlist row with the same phone, else
   *  the phone number itself. Null only when we hold none of the three. */
  name: string | null;
  phone: string | null;
  onboarding_step: string | null;
  has_profile: boolean;
  is_team: boolean;
  their_texts: number;
  first_text: string | null;
  last_text: string | null;
  we_replied: boolean;
  /** What the coach still needs before it can build them a plan, in the same
   *  words the coach itself uses — it asks the real gate, not a copy of it. */
  still_needed: string[];
  has_plan: boolean;
  /** `null` means PostHog was unreachable, NOT that they never opened it. */
  opened_app: boolean | null;
}

export interface ImessageFunnelStep {
  key: string;
  label: string;
  count: number;
  team: number;
  names: string[];
  /** True when the count could not be resolved at all (PostHog down for the
   *  "opened the app" bar). The page must render a blank, never a 0. */
  unknown?: boolean;
}

export interface ImessageHealth {
  runs: number;
  by_status?: Record<string, number>;
  replied_runs?: number;
  reply_p50_seconds?: number | null;
  reply_p90_seconds?: number | null;
  first_text_at?: string | null;
  bots_excluded?: number;
}

export interface ImessageMetrics {
  days: number;
  since: string;
  funnel: ImessageFunnelStep[];
  health: ImessageHealth;
  people: ImessagePerson[];
  /** Every event the Linq telemetry module can emit, zeros included — a zero
   *  here is the difference between "nobody did that" and "never wired up". */
  posthog: { event: string; count: number; people: number; wired: boolean }[] | null;
  posthog_error?: string | null;
  generated_at: string;
}

export async function fetchImessageMetrics(days = 30): Promise<ImessageMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/imessage_metrics?days=${days}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.detail || 'Failed to load iMessage metrics');
  }
  return response.json();
}

// Nutrition playground (admin → Playground → Nutrition tab)

/** The nutrition-only inputs the workout profile builder doesn't carry.
 *  Every one is read server-side from a real member's record, so the
 *  playground supplies them by hand. `null`/omitted means "not on file" —
 *  a case worth testing deliberately (an undeclared goal should make the
 *  engine run maintenance and SAY so, not silently pick a direction). */
export interface NutritionOverrides {
  nutrition_goal?: string | null;
  training_phase?: number | null;
  sessions_per_week?: number | null;
  is_training_day?: boolean;
  readiness_state?: string | null;
  readiness_cause?: string | null;
  diet_restriction?: string | null;
  current_food_list?: string[];
  excluded_foods?: string[];
  /** Simulate an unreadable profile row — the agent's fail-closed path. */
  profile_missing?: boolean;
}

/** The nutrition specialist's envelope — the raw result the coach relays.
 *  Shape mirrors NutritionResult.to_result_dict() in agent/nutrition_loop.py. */
export interface NutritionEnvelope {
  status?: string;
  confidence?: string;
  summary?: string;
  user_message?: string;
  targets?: Record<string, unknown> | null;
  audit?: Record<string, unknown> | null;
  recommendations?: Record<string, unknown> | null;
  missing_inputs?: string[];
  ask_bundle?: Record<string, unknown> | null;
  goal_confirmation?: Record<string, unknown> | null;
  applied_gates?: string[];
  assumptions?: string[];
  tools_called?: string[];
  degraded?: string | null;
  error_code?: string | null;
  duration_ms?: number;
}

export interface NutritionChatTurn {
  /** The coach's prose — what a member would actually read. Null in
   *  specialist-only mode, or if the turn was truncated. */
  coach_reply: string | null;
  /** The specialist's envelope. Null when the coach never delegated. */
  specialist: NutritionEnvelope | null;
  delegations: Array<{ request: string; scope_hint?: string | null; status?: string }>;
  delegated: boolean;
  context_used: Record<string, unknown>;
  /** Saves the agent ATTEMPTED — recorded and discarded, never executed. */
  attempted_writes: Array<{ table: string; op: string; payload: unknown }>;
  doctrine_source: string;
  model: string;
  specialist_only: boolean;
  truncated: boolean;
  /** Ways this run is not production. Rendered on the page so nobody has to
   *  read the router to know what they're looking at. */
  fidelity_notes: string[];
  duration_ms: number;
}

export interface NutritionChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Run one conversational turn against an invented member.
 *  Reads and writes no account: the context is injected and the Supabase
 *  handle the agent gets records writes without executing them. */
export async function runNutritionPlaygroundChat(args: {
  profile: PlaygroundProfile;
  overrides: NutritionOverrides;
  message: string;
  history: NutritionChatMessage[];
  specialistOnly?: boolean;
}): Promise<NutritionChatTurn> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/playground/nutrition_chat`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        profile: args.profile,
        overrides: args.overrides,
        message: args.message,
        history: args.history,
        specialist_only: !!args.specialistOnly,
      }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.detail || err.error || `Nutrition chat failed (HTTP ${response.status})`,
    );
  }
  return response.json();
}

// RevenueCat reconciliation (admin → RevenueCat tab)
//
// Answers "who is actually paying us, by name?" — which RevenueCat's own
// dashboard can't, because it keys customers on App User ID (our Supabase UUID)
// and $displayName attributes set via its REST API never render.

export interface RcPurchaser {
  id: string;
  name: string | null;
  is_anonymous: boolean;
  has_profile: boolean;
  products: string[];
  stores: string[];
  period_types: string[];
  sandbox_only: boolean;
  active_entitlements: string[];
  expires_at: string | null;
  unsubscribe_detected_at: string | null;
  rc_display_name: string | null;
  name_source: 'profile' | 'revenuecat' | null;
  /** When they first started paying us — survives plan switches. */
  started_paying_at: string | null;
  billing_issue_at: string | null;
  /** Server-derived lifecycle state; never re-derive this in the UI.
   *  `trial_expired` never paid us — it is NOT churn. */
  state:
    | 'active'
    | 'cancelling'
    | 'billing_issue'
    | 'trialing'
    | 'churned'
    | 'trial_expired';
  /** Products earning right now. Trials and expired plans are excluded. */
  paid_products: string[];
  trial_products: string[];
  ever_paid: boolean;
  our_status: string | null;
  our_plan: string | null;
  entitlement_mismatch: boolean;
  plan_unmapped: boolean;
  is_internal: boolean;
  monthly_value_usd: number;
}

export interface RcOverview {
  cached: boolean;
  scanned_at_epoch: number;
  elapsed_seconds: number;
  project_id: string;
  scope: 'identified_only' | 'all_customers';
  counts: {
    total_customers: number;
    anonymous: number;
    identified: number;
    probed: number;
    purchasers: number;
    real_purchasers: number;
    sandbox_only_purchasers: number;
    unnamed_real_purchasers: number;
    anonymous_purchasers: number;
    entitlement_mismatches: number;
    plan_unmapped: number;
    purchasers_without_profile: number;
    live_paying_members: number;
    mrr_usd: number;
    arr_usd: number;
    internal_members_excluded: number;
    internal_allowlist_size: number;
    cancelling: number;
    billing_issues: number;
    /** Lost PAYING members. Expired trials are counted separately. */
    churned: number;
    trialing: number;
    /** Trials that ran out without converting — never paid, so never churn. */
    trial_expired: number;
    /** Share of everyone who ever paid that no longer does. Null before anyone has. */
    paid_churn_rate_pct: number | null;
    ever_paid_members: number;
    /** Money actually billed by the stores, external only. Exact, not a list price. */
    realized_gross_usd: number;
    /** What actually landed after store commission + tax. */
    realized_proceeds_usd: number;
    /** Gross collected per paying member TO DATE. Deliberately not called LTV:
     *  it climbs as members keep renewing, and a real LTV needs a churn rate
     *  (currently 0%, so undefined). See the note in revenuecat_admin_service. */
    revenue_per_member_usd: number | null;
    /** Monthly revenue already told to stop — cancellations + billing failures. */
    mrr_at_risk_usd: number;
  };
  /** Weekly history from RevenueCat's own charts, so our MRR definition and
   *  theirs can never drift apart. Empty series when charts are unavailable. */
  trend: {
    mrr: RcTrendPoint[];
    actives: RcTrendPoint[];
    start_date: string;
    error: string | null;
  };
  /** Lifetime value, or why it isn't computable yet. Populates automatically once
   *  `churn_events` reaches `churn_events_required` — no code change needed. */
  ltv: RcLtv;
  real_purchasers: RcPurchaser[];
  sandbox_purchasers: RcPurchaser[];
}

export interface RcLtv {
  available: boolean;
  /** Gross LTV. Null until available. */
  usd: number | null;
  /** After the store's measured cut — the figure to set acquisition spend against. */
  net_usd: number | null;
  avg_lifetime_months: number | null;
  avg_retention_years: number | null;
  arpu_usd: number | null;
  revenue_per_year_usd: number | null;
  monthly_churn_rate_pct: number | null;
  churn_events: number;
  churn_events_required: number;
  /** Member-months of observation accumulated so far — the denominator. */
  exposure_months: number;
  blocked_reason:
    | 'no_churn_yet'
    | 'too_few_churn_events'
    | 'insufficient_data'
    | null;
  method: string;
}

export interface RcTrendPoint {
  week: string;
  value: number;
  /** The current week is still accruing — render it as provisional. */
  incomplete: boolean;
}

export async function getRevenueCatOverview(opts?: {
  includeAnonymous?: boolean;
  refresh?: boolean;
}): Promise<RcOverview> {
  const params = new URLSearchParams();
  if (opts?.includeAnonymous) params.set('include_anonymous', 'true');
  if (opts?.refresh) params.set('refresh', 'true');
  const qs = params.toString();
  const url = `${BACKEND_API_URL}/admin/revenuecat/overview${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers: await authHeaders() });

  // A bare 404 here almost always means the backend process is older than this
  // route, not that anything is misconfigured — FastAPI answers {"detail":
  // "Not Found"}, which is useless on its own. Name the URL and the likely
  // cause so this is self-diagnosing instead of a round trip.
  if (response.status === 404) {
    throw new Error(
      `404 from ${url} — that backend doesn't have this route. ` +
        `Restart the backend (a process started before this branch won't have it), ` +
        `and check VITE_BACKEND_API_URL points where you expect.`,
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // 503 carries an actionable "which env var is missing" detail.
    throw new Error(
      data.detail || data.error || `HTTP ${response.status} from ${url}`,
    );
  }
  return data as RcOverview;
}

// Wearables inspector (admin -> Wearables)
//
// These MUST go through the backend: wearable_data / wearable_connections are
// RLS'd to auth.uid() = user_id, so querying them with the browser's Supabase
// client returns only the admin's own rows and would render a convincingly
// empty dashboard. The backend reads with the service-role client.

export interface WearableField {
  key: string
  seen: number
  populated: number
  populated_pct: number
  example: unknown
}

export interface WearableDataType {
  data_type: string
  rows_sampled: number
  users: number
  oldest: string | null
  newest: string | null
  last_written: string | null
  fields: WearableField[]
  sample_payload: unknown
}

export interface WearableProviderReport {
  sampled: number
  sample_limit: number
  truncated: boolean
  data_types: WearableDataType[]
}

export interface WearableConnectionRow {
  id: string
  user_id: string
  display_name: string | null
  provider: string
  is_active: boolean
  has_terra_user_id: boolean
  connected_at: string | null
  last_sync_at: string | null
  initial_fetch_completed: boolean | null
  latest_data_at: string | null
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BACKEND_API_URL}${path}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.error || body.detail || `Request failed: ${response.status}`)
  }
  return response.json()
}

export async function getWearablesOverview(): Promise<Record<string, WearableProviderReport>> {
  const data = await getJson<{ providers: Record<string, WearableProviderReport> }>(
    "/admin/wearables/overview",
  )
  return data.providers
}

export async function getWearableConnections(): Promise<WearableConnectionRow[]> {
  const data = await getJson<{ connections: WearableConnectionRow[] }>(
    "/admin/wearables/connections",
  )
  return data.connections
}

export interface WearableReading {
  data_type: string
  recorded_at: string
  provider: string
  payload: unknown
  created_at: string | null
  updated_at: string | null
}

export interface WearableUserDetail {
  user: { id: string; display_name: string | null }
  connections: {
    provider: string
    is_active: boolean
    connected_at: string | null
    last_sync_at: string | null
    initial_fetch_completed: boolean | null
  }[]
  rows: WearableReading[]
  row_limit: number
  truncated: boolean
}

export async function getWearableUserDetail(
  userId: string,
  provider?: string,
): Promise<WearableUserDetail> {
  const qs = provider ? `?provider=${encodeURIComponent(provider)}` : ""
  return getJson<WearableUserDetail>(
    `/admin/wearables/user/${encodeURIComponent(userId)}${qs}`,
  )
}

/**
 * Day-to-day activity for the dashboard's General card: who opens the app,
 * where they go, what they tap, and what they say to us first.
 *
 * Sessions and screens are PostHog; chips and first messages come from Supabase
 * `chat_messages`, because chat text never reaches PostHog. Either half can
 * fail on its own — check `posthog_error` / `chat_error` before rendering that
 * half, rather than treating the whole card as dead.
 */
export interface GeneralMetrics {
  configured: boolean;
  days?: number;
  /**
   * Set when PostHog didn't answer — outages are routine (503/504/read timeout,
   * dozens a month). There is deliberately no plain `error` field: one used to
   * sit here, the endpoint never returned it, and the UI's outage banner was
   * wired to it, so every outage rendered as zeros. Removing it makes TypeScript
   * catch the next person who reaches for the wrong name.
   */
  posthog_error?: string | null;
  /**
   * ISO timestamp. Set when PostHog was unreachable and the backend served the
   * last answer it had rather than nothing. The numbers below are then REAL but
   * OLD, and the UI has to say so — an undated aged number misleads exactly as
   * badly as an unlabelled zero.
   */
  posthog_stale_as_of?: string | null;
  chat_error?: string | null;
  /**
   * One row per day, oldest first. Sessions are app-only (backend events carry
   * no session). `users` is that day's distinct people; `wau` is the trailing
   * SEVEN days' distinct people, which the server has to compute because a
   * week of actives is a union and not a sum of `users`. A day nobody opened
   * the app has no row at all, and the first six rows of the window have less
   * than a week behind them, so `wau` only reads true on the tail.
   */
  daily?: {
    date: string;
    sessions: number;
    users: number;
    wau?: number;
    /**
     * Who those `users` were, by name — `users` is already spent on the count
     * here, unlike every other named row on this page. Capped server-side at
     * 200 names, so a busy day names its first 200 and no more.
     */
    names?: string[];
  }[];
  /**
   * In-app screens only — sign-up, log-in, questionnaire and paywall are out.
   * `users` names the same distinct people `people` counts, so a bar can be
   * turned into a call list. Capped server-side at 200 names.
   */
  screens?: {
    screen: string;
    label: string;
    views: number;
    people: number;
    users?: string[];
  }[];
  /**
   * An ALLOW-list of member-initiated taps, each with a plain-English `label`.
   * NOT a complete picture of tapping — autocapture is off, so only the
   * handful of hand-written `capture()` calls show up here at all.
   */
  actions?: {
    event: string;
    label: string;
    count: number;
    people: number;
    users?: string[];
  }[];
  /**
   * What HAPPENED, as opposed to what somebody did — successes, failures and
   * things the app showed people. Kept out of `actions` on purpose (a callback
   * firing is not a tap), and it is the half that shows a break: 13 people have
   * tapped connect-calendar all-time and 9 ever got connected.
   */
  outcomes?: {
    event: string;
    label: string;
    count: number;
    people: number;
    users?: string[];
  }[];
  /**
   * The denominator for every "N people did X" row: distinct in-app sessions
   * and people across the WHOLE window, plus `roster` — how many accounts the
   * selected cohorts hold. Not an "app opens" count on purpose: the lifecycle
   * events either fire before the app identifies who it is talking to (so they
   * reach 8 of 20) or fire again on every foreground return (94 events, 41
   * sessions). `roster` is filled in even when PostHog fails.
   */
  app_use?: { sessions: number; people: number; roster: number };
  /**
   * Calendar connect counted PER PERSON, because "did it work" is the whole
   * question there. Cannot be derived by subtracting the rows above — one
   * member tapped 20 times, failed 11 and succeeded 3, so tapped − connected −
   * failed lands on 1 silent drop where there are 3. `silent` is the worst
   * bucket: they tapped and the app said nothing back, success or failure.
   */
  calendar_funnel?: {
    tapped: number;
    connected: number;
    failed_only: number;
    silent: number;
  };
  /**
   * PostHog's frustration signal: the same spot jabbed repeatedly. Not a tap
   * anyone meant. `screens` says WHERE — the event names no element and no
   * screen of its own, so the backend reads it off the session timeline (the
   * last screen seen before the jab); "unknown" is a session that stamped no
   * screen at all, not a stale app. `walked` says WHAT IT COST: jabs after
   * which the person did nothing else. Per-screen `people` and `walked_people`
   * never sum to a total — one person jabbing on two screens is one frustrated
   * person, not two.
   */
  rage?: {
    taps: number;
    people: number;
    screens?: {
      screen: string;
      label: string;
      taps: number;
      people: number;
      /** Jabs after which the person did nothing else — they left. */
      walked: number;
      walked_people: number;
    }[];
    /**
     * The jabs themselves, newest first — the only surface that can say WHAT
     * got hit, because no property ever will: `replay` opens PostHog's
     * recording of that session 8 seconds before the tap, so you watch it.
     * Absent when there is no recording to open. `users` is the one person,
     * resolved from the same profiles read as every other row on this card.
     */
    jabs?: {
      screen: string;
      label: string;
      at: string;
      walked: boolean;
      replay?: string;
      users?: string[];
    }[];
    /**
     * WHY, since WHERE is unavailable. Each jab placed against that person's
     * chat timeline. `waiting` is the strong one: their message went out
     * within 3 minutes and no coach reply had landed yet. `mid_chat` is the
     * same window WITHOUT the reply check — only "in a conversation", never
     * to be labelled as waiting. `resends` is a send landing within 10s AFTER
     * the jab, i.e. the send button itself was what got jabbed. All three
     * divide by `jabs`, which travels with them so the panel never divides by
     * a `taps` from a different read.
     */
    chat?: {
      jabs: number;
      waiting: number;
      waiting_people: number;
      resends: number;
      resend_people: number;
      mid_chat: number;
    };
  };
  /** Which door people came through to reach the coach. */
  chat_opens?: {
    source: string;
    label: string;
    count: number;
    people: number;
    users?: string[];
  }[];
  /**
   * Wearables actually connected, from `wearable_connections` — not PostHog.
   * The tap that STARTS the flow under-counts the outcome: it only exists in
   * builds that ship it, and the At a Glance route fires a different event.
   * There IS a `wearable_connect_succeeded` event (in `outcomes`) and it agrees
   * with this table exactly — 7 people both sides in the cohort-2 window,
   * 2026-08-28 — which is the cross-check that this number is right.
   * Same window, same cohort, same exclusions as the PostHog rows beside it.
   */
  connected_wearables?: {
    people: number;
    devices: number;
    users?: string[];
  } | null;
  /**
   * Calendar / MindBody / studio logins, read as STATE from Postgres columns
   * rather than from a connect tap — the tap counts intent and misses anyone
   * who linked in a build older than the event. Takes no window: nobody links
   * an account before the cohort they joined in, so the cohort already IS the
   * window. The cost is history — someone who linked and later unlinked is
   * absent — so read a row as "linked right now", never "ever linked".
   *
   * A row is OMITTED when its own read failed, which the card shows as "—", not
   * 0. Emptiness is the failure signal, never absence of the key: the backend
   * always sends the list, and each of the three reads drops only its own row,
   * so a locked `site_credentials` no longer blanks the two calendar rows.
   * Match on `id` and take the label from the local `RowSpec` — the server
   * deliberately sends numbers only.
   */
  linked_accounts?: {
    /** Stable key the dashboard matches on, so a relabel can't unhook a row. */
    id: string;
    people: number;
    /**
     * The thing itself, where a person can hold several — a studio login PER
     * STUDIO. Equal to `people` where they can't (one calendar each).
     */
    accounts?: number;
    users?: string[];
  }[];
  /** Canned prompt chips, recovered by matching the chip's exact text in chat. */
  chips?: { text: string; taps: number; people: number; as_first: number }[];
  first_messages?: {
    users: number;
    /** Opened with a chip. */
    canned: number;
    /** Typed something of their own. */
    typed: number;
    samples: { name: string; text: string; when: string }[];
  };
}

export async function getGeneralMetrics(days = 30, cohorts?: number[]): Promise<GeneralMetrics> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/general_metrics?days=${days}${cohortQ(cohorts)}`,
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new Error('Failed to load general metrics');
  return response.json();
}

export interface ReferralRow {
  code: string
  joined_at: string
  inviter_id: string
  inviter_name: string
  joined_id: string
  joined_name: string
  relationship_state: string | null
  notice: string
  notice_detail: string | null
  report_run_id: string | null
}

export interface ReferralAttemptRow {
  created_at: string
  member_id: string
  member_name: string
  code: string
  code_owner_id: string
  code_owner_name: string
  identity_outcome: string
  outcome: string
}

export async function getReferrals(): Promise<ReferralRow[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/referrals`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load referrals');
  }
  const body = await response.json();
  return body.referrals;
}

export async function getReferralAttempts(): Promise<ReferralAttemptRow[]> {
  const response = await fetch(`${BACKEND_API_URL}/admin/referrals/attempts`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || err.error || 'Failed to load referral attempts');
  }
  const body = await response.json();
  return body.attempts;
}
